import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { ChevronLeft, Download, FolderOpen, Sparkles, SlidersHorizontal } from 'lucide-react';

// UI primitives
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Hooks (all backend contracts live here — reused as-is)
import { useToast } from '@/hooks/use-toast';
import { useCredits, CREDIT_OPERATIONS, CreditOperation } from '@/hooks/useCredits';
import { useSubscription } from '@/hooks/useSubscription';
import { useMovConversion } from '@/hooks/useMovConversion';
import { useYouTubePublish } from '@/hooks/useYouTubePublish';
import { useBackgroundRemoval } from '@/hooks/useBackgroundRemoval';
import { useAudioEnhancement } from '@/hooks/useAudioEnhancement';
import { useBackgroundReplacement } from '@/hooks/useBackgroundReplacement';
import { useScriptAnalysis } from '@/hooks/useScriptAnalysis';
import { usePlanMode } from '@/hooks/usePlanMode';

// Reused components
import { CreditConfirmDialog } from '@/components/credits/CreditConfirmDialog';
import { CreditPurchaseModal } from '@/components/credits/CreditPurchaseModal';
import { UpgradeModal } from '@/components/subscription';
import { LoginModal } from '@/components/LoginModal';
import { CastingVideoImporter } from '@/components/CastingVideoImporter';
import { AIAssetGenerator } from '@/components/video-editor/AIAssetGenerator';
import { SchedulePostModal } from '@/components/social/SchedulePostModal';
import { BackgroundReplacementWizard } from '@/components/video-editor/BackgroundReplacementWizard';
import { RenderHistory } from '@/components/video-editor/RenderHistory';
import { QuickPreviewPanel } from '@/components/video-editor/QuickPreviewPanel';
import { DEFAULT_COLOR_ADJUSTMENTS, hasColorAdjustments, type ColorAdjustments } from '@/components/video-editor/RenderControlsPanel';
import type { ProjectClip } from '@/components/video-editor/TimelineSequence';
import type { VideoTemplate } from '@/components/templates';
import LibraryPicker, { probeVideoUrl, stagedFileName, type StagedLibraryFile } from '@/components/library/LibraryPicker';

// New enhancer surfaces
import { EnhancerTopBar, type EnhancerMode } from './EnhancerTopBar';
import { AssetsPanel } from './AssetsPanel';
import { AICreatePanel } from './AICreatePanel';
import { TimelinePanel } from './TimelinePanel';
import { YouTubePublishDialog } from './YouTubePublishDialog';

// Contracts
import { resolveVideoUrl, resolveOutputConfig } from '@/lib/utils';
import { buildRenderAssets, findFileAnalysis, type EnhancerFile } from '@/lib/enhancerMeta';
import type { RenderedVideo, QuickPreview } from '@/types/video-enhancer';

// ---------------------------------------------------------------------------
// The Video Enhancer, rebuilt: one responsive page, three zones (sticky top
// bar / assets / mode panel) plus render history. All backend contracts are
// preserved verbatim — see lib/enhancerMeta.ts for the AI-pipeline payloads.
// ---------------------------------------------------------------------------

const randId = () => Math.random().toString(36).substr(2, 9);
const hasAuthCookie = () =>
  document.cookie.includes('token=') || document.cookie.includes('authToken=') || document.cookie.includes('castingAuthToken=');

const cardStyle: React.CSSProperties = {
  padding: 18,
  borderRadius: 'var(--hj-r-md)',
  background: 'var(--hj-surface-1)',
  border: '1px solid var(--hj-hairline)',
};

const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
const filterExpiredRenders = (renders: RenderedVideo[]): RenderedVideo[] => {
  const now = Date.now();
  return (renders || []).filter((video: any) => {
    if (video.status === 'pending' || video.status === 'processing' || video.status === 'rendering') return true;
    return (now - new Date(video.timestamp).getTime()) < TWENTY_FOUR_HOURS;
  });
};

const loadLocal = <T,>(key: string, fallback: T): T => {
  if (!hasAuthCookie()) return fallback;
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch {
    return fallback;
  }
};

export const VideoEnhancerApp: React.FC = () => {
  const { toast } = useToast();
  const { checkCredits, fetchBalance, isAuthenticated: isCreditUser, getCreditDisplay } = useCredits();
  const { isSubscriber } = useSubscription();
  const { needsConversion, convert: convertMov } = useMovConversion();

  // --- Credit gating -------------------------------------------------------
  const [creditConfirmOpen, setCreditConfirmOpen] = useState(false);
  const [creditConfirmOperation, setCreditConfirmOperation] = useState<CreditOperation>(CREDIT_OPERATIONS.SHOTSTACK_RENDER);
  const [creditConfirmOperationName, setCreditConfirmOperationName] = useState('');
  const [creditConfirmCallback, setCreditConfirmCallback] = useState<(() => void) | null>(null);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const requireCreditConfirmation = async (
    operationType: CreditOperation,
    operationName: string,
    onConfirm: () => void,
  ) => {
    if (!isCreditUser()) { onConfirm(); return; }
    const creditCheck = await checkCredits(operationType);
    if (creditCheck?.isFree) { onConfirm(); return; }
    if (!isSubscriber) { setShowUpgradeModal(true); return; }
    if (creditCheck?.hasCredits) {
      setCreditConfirmOperation(operationType);
      setCreditConfirmOperationName(operationName);
      setCreditConfirmCallback(() => onConfirm);
      setCreditConfirmOpen(true);
    } else {
      toast({
        title: 'Not enough video tokens',
        description: `You need ${creditCheck?.cost || 1} video token(s) for ${operationName}. Please purchase more video tokens.`,
        variant: 'destructive',
      });
      setShowCreditPurchaseModal(true);
    }
  };

  // --- Project persistence (MongoDB /api/projects; localStorage fallback) ---
  const apiBase = '/api/projects';

  const fetchProjects = async () => {
    try {
      const response = await fetch(apiBase, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch projects');
      const data = await response.json();
      return data.projects || [];
    } catch {
      return [];
    }
  };

  const saveProject = async (projectId: string, updates: any) => {
    try {
      const response = await fetch(`${apiBase}/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  const createProject = async (name: string) => {
    try {
      const response = await fetch(apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name }),
      });
      if (!response.ok) throw new Error('Failed to create project');
      const data = await response.json();
      return data.project;
    } catch {
      return null;
    }
  };

  const deleteProjectApi = async (projectId: string) => {
    try {
      const response = await fetch(`${apiBase}/${projectId}`, { method: 'DELETE', credentials: 'include' });
      return response.ok;
    } catch {
      return false;
    }
  };

  const [useDatabase, setUseDatabase] = useState(false);
  const [dbProjects, setDbProjects] = useState<any[]>([]);
  const [currentDbProject, setCurrentDbProject] = useState<any>(null);

  // --- Core state ----------------------------------------------------------
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => document.cookie.includes('token='));

  const [projects, setProjects] = useState<string[]>(() => {
    const list = loadLocal<string[]>('projects_NLE', ['default']);
    if (!list.includes('default')) list.unshift('default');
    return list;
  });
  const [currentProject, setCurrentProject] = useState<string>(() =>
    hasAuthCookie() ? (localStorage.getItem('currentProject_NLE') || 'default') : 'default');

  const initialProjectKey = hasAuthCookie() ? (localStorage.getItem('currentProject_NLE') || 'default') : 'default';
  const [files, setFiles] = useState<EnhancerFile[]>(() => loadLocal(`project_${initialProjectKey}_files`, []));
  const [clips, setClips] = useState<ProjectClip[]>(() => loadLocal(`project_${initialProjectKey}_clips`, []));
  const [renderedVideos, setRenderedVideos] = useState<RenderedVideo[]>(() => loadLocal(`project_${initialProjectKey}_rendered`, []));
  const [savedRenders, setSavedRenders] = useState<RenderedVideo[]>(() => loadLocal(`project_${initialProjectKey}_savedRenders`, []));
  const [analysisResults, setAnalysisResults] = useState<{ [key: string]: any }>(() => loadLocal(`project_${initialProjectKey}_analysis`, {}));
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(() =>
    new Set(loadLocal<EnhancerFile[]>(`project_${initialProjectKey}_files`, []).map(f => f.id)));

  const [renderMode, setRenderMode] = useState<EnhancerMode>(() => {
    const saved = localStorage.getItem('renderMode');
    return (saved === 'lambda' || saved === 'direct') ? saved : 'lambda'; // AI Create is the default
  });
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [creativeBrief, setCreativeBrief] = useState('');
  const [exportFilter, setExportFilter] = useState<string>('none');
  const [colorAdjustments, setColorAdjustments] = useState<ColorAdjustments>({ ...DEFAULT_COLOR_ADJUSTMENTS });

  // Master prompt (RenderControlsPanel contract)
  const [showMasterPrompt, setShowMasterPrompt] = useState(false);
  const [editableMasterPrompt, setEditableMasterPrompt] = useState('');
  const [isEditingMasterPrompt, setIsEditingMasterPrompt] = useState(false);

  // Modals / panels
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [showImportChoice, setShowImportChoice] = useState(false);
  const [pendingImportData, setPendingImportData] = useState<{ videos: any[]; projectName: string } | null>(null);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [aiGeneratingJob, setAiGeneratingJob] = useState<{ jobId: string; type: 'image' | 'video' | 'music'; status: string; progress: number; assetUrl?: string } | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleVideoData, setScheduleVideoData] = useState<{ videoUrl: string; thumbnailUrl?: string; renderId?: string } | null>(null);
  const [showTemplatesPanel, setShowTemplatesPanel] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<VideoTemplate | null>(null);
  const [selectedJsonView, setSelectedJsonView] = useState<RenderedVideo | null>(null);
  const [selectedVideoModal, setSelectedVideoModal] = useState<RenderedVideo | null>(null);
  const [previewClip, setPreviewClip] = useState<{ clip: ProjectClip; file: EnhancerFile } | null>(null);
  const [showModeExplainer, setShowModeExplainer] = useState(false);

  // AI generation / preview state
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [isSubmittingRender, setIsSubmittingRender] = useState(false);
  const [quickPreview, setQuickPreview] = useState<QuickPreview | null>(null);
  const [showQuickPreviewPanel, setShowQuickPreviewPanel] = useState(false);

  // Refs (stale-closure fixes + forward wiring)
  const renderedVideosRef = useRef<RenderedVideo[]>(renderedVideos);
  useEffect(() => { renderedVideosRef.current = renderedVideos; }, [renderedVideos]);
  const analysisResultsRef = useRef<{ [key: string]: any }>(analysisResults);
  useEffect(() => { analysisResultsRef.current = analysisResults; }, [analysisResults]);
  const pollRenderStatusRef = useRef<(videoId: string, renderId: string) => void>(() => {});
  const applyAIEditToTimelineRef = useRef<(json: any, title: string) => void>(() => {});

  const updateRender = (videoId: string, updates: Partial<RenderedVideo>) =>
    setRenderedVideos(prev => prev.map(v => (v.id === videoId ? { ...v, ...updates } : v)));

  // --- Extracted hooks -----------------------------------------------------
  const youtube = useYouTubePublish({ toast: toast as any });

  const bgRemoval = useBackgroundRemoval({
    files: files as any,
    setFiles: setFiles as any,
    toast: toast as any,
    requireCreditConfirmation,
    CREDIT_OPERATIONS,
  });

  const audioEnhancement = useAudioEnhancement({
    renderedVideos,
    setRenderedVideos,
    toast: toast as any,
    requireCreditConfirmation,
    CREDIT_OPERATIONS,
  });

  const bgReplacement = useBackgroundReplacement({
    renderedVideos,
    setRenderedVideos,
    toast: toast as any,
    requireCreditConfirmation,
    CREDIT_OPERATIONS,
  });

  const scriptHook = useScriptAnalysis({
    files: files as any,
    analysisResults,
    currentProject,
    aspectRatio,
    toast: toast as any,
    onCreativeBriefChange: (brief: string) => setCreativeBrief(brief),
    applyAIEditToTimeline: (...args: [any, string]) => applyAIEditToTimelineRef.current(...args),
  });

  const planMode = usePlanMode({
    files: files as any,
    selectedFiles,
    analysisResults,
    scriptContent: scriptHook.scriptContent,
    currentProject,
    aspectRatio,
    toast: toast as any,
    onRenderCreated: (video: RenderedVideo) => setRenderedVideos(prev => [...prev, video]),
    onRenderUpdated: updateRender,
    pollRenderStatus: (...args: [string, string]) => pollRenderStatusRef.current(...args),
  });

  const { scriptContent, scriptAnalysis, loadProjectScriptData, resetScriptState, setShowScriptPanel } = scriptHook;
  const { planModeActive, finishingTouches, resetPlanState } = planMode;

  // --- Initialization ------------------------------------------------------
  useEffect(() => {
    const initializeProjects = async () => {
      // Migrate any orphaned projects first
      try {
        await fetch('/api/projects/migrate', { method: 'POST', credentials: 'include' });
      } catch { /* non-fatal */ }

      const fetched = await fetchProjects();
      if (fetched && fetched.length > 0) {
        setUseDatabase(true);
        const seen = new Set<string>();
        const unique = fetched.filter((p: any) => {
          if (seen.has(p.name)) return false;
          seen.add(p.name);
          return true;
        });
        setDbProjects(unique);
        setProjects(unique.map((p: any) => p.name));

        const savedCurrent = localStorage.getItem('currentProject_NLE');
        const projectToLoad = unique.find((p: any) => p.name === savedCurrent) || unique[0];
        if (projectToLoad) {
          setCurrentProject(projectToLoad.name);
          setCurrentDbProject(projectToLoad);
          setFiles(projectToLoad.files || []);
          setSelectedFiles(new Set((projectToLoad.files || []).map((f: any) => f.id)));
          setClips(projectToLoad.clips || []);
          setRenderedVideos(filterExpiredRenders(projectToLoad.renders || []));
          setSavedRenders(projectToLoad.savedRenders || []);
          setAnalysisResults(projectToLoad.analysisResults || {});
        }
        toast({ title: 'Connected to cloud storage', description: 'Your projects are synced across devices' });
      } else if (isAuthenticated) {
        const defaultProject = await createProject('Default Project');
        if (defaultProject) {
          setUseDatabase(true);
          setDbProjects([defaultProject]);
          setCurrentProject(defaultProject.name);
          setCurrentDbProject(defaultProject);
        }
      }
    };
    if (isAuthenticated) initializeProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Clear everything on logout (auth true → false)
  const prevAuthRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevAuthRef.current === true && !isAuthenticated) {
      setProjects(['default']);
      setCurrentProject('default');
      setFiles([]);
      setClips([]);
      setSelectedFiles(new Set());
      setRenderedVideos([]);
      setSavedRenders([]);
      setAnalysisResults({});
      setUseDatabase(false);
      setDbProjects([]);
      setCurrentDbProject(null);
    }
    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Persist render mode
  useEffect(() => { localStorage.setItem('renderMode', renderMode); }, [renderMode]);

  // First-visit mode explainer (same flag as the legacy welcome dialog)
  useEffect(() => {
    if (!localStorage.getItem('hasSeenWelcomeDialog')) setShowModeExplainer(true);
  }, []);

  // Debounced project save — MongoDB when available, localStorage as the
  // unauthenticated/offline fallback. analysisData is stripped from files and
  // clips to prevent 413 Content Too Large (full transcripts bloat the doc).
  useEffect(() => {
    const saveTimer = setTimeout(async () => {
      if (useDatabase && currentDbProject) {
        const filesWithoutAnalysis = files.map(({ analysisData, ...file }) => file);
        const clipsWithoutAnalysis = clips.map(({ analysisData, ...clip }: any) => clip);
        await saveProject(currentDbProject._id, {
          files: filesWithoutAnalysis,
          clips: clipsWithoutAnalysis,
          renders: renderedVideos,
          analysisResults,
          savedRenders,
        });
      } else {
        try {
          localStorage.setItem(`project_${currentProject}_files`, JSON.stringify(files));
          localStorage.setItem(`project_${currentProject}_clips`, JSON.stringify(clips));
          localStorage.setItem(`project_${currentProject}_rendered`, JSON.stringify(renderedVideos));
          localStorage.setItem(`project_${currentProject}_savedRenders`, JSON.stringify(savedRenders));
          localStorage.setItem(`project_${currentProject}_analysis`, JSON.stringify(analysisResults));
        } catch (error) {
          console.error('Failed to save project data to localStorage:', error);
        }
      }
    }, 1000);
    return () => clearTimeout(saveTimer);
  }, [files, clips, renderedVideos, analysisResults, savedRenders, currentProject, useDatabase, currentDbProject]);

  // Persist projects list (fallback storage)
  useEffect(() => {
    try { localStorage.setItem('projects_NLE', JSON.stringify(projects)); } catch { /* noop */ }
  }, [projects]);

  // Auto-delete expired renders (24h) — in-progress renders exempt
  useEffect(() => {
    const cleanup = () => setRenderedVideos(prev => {
      const valid = filterExpiredRenders(prev);
      return valid.length !== prev.length ? valid : prev;
    });
    cleanup();
    const interval = setInterval(cleanup, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Keep the editable master prompt in sync while not editing
  useEffect(() => {
    if (!isEditingMasterPrompt) setEditableMasterPrompt(generateMasterPrompt());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clips, isEditingMasterPrompt, scriptContent]);

  // --- Project management --------------------------------------------------
  const handleProjectChange = (projectName: string) => {
    if (useDatabase) {
      const project = dbProjects.find(p => p.name === projectName);
      if (!project) return;
      setCurrentProject(projectName);
      setCurrentDbProject(project);
      setFiles(project.files || []);
      setSelectedFiles(new Set((project.files || []).map((f: any) => f.id)));
      setClips(project.clips || []);
      setRenderedVideos(filterExpiredRenders(project.renders || []));
      setSavedRenders(project.savedRenders || []);
      setAnalysisResults(project.analysisResults || {});
      localStorage.setItem('currentProject_NLE', projectName);
    } else {
      localStorage.setItem('currentProject_NLE', projectName);
      setCurrentProject(projectName);
      const loadedFiles = loadLocal<EnhancerFile[]>(`project_${projectName}_files`, []);
      setFiles(loadedFiles);
      setSelectedFiles(new Set(loadedFiles.map(f => f.id)));
      setClips(loadLocal(`project_${projectName}_clips`, []));
      setRenderedVideos(loadLocal(`project_${projectName}_rendered`, []));
      setSavedRenders(loadLocal(`project_${projectName}_savedRenders`, []));
      setAnalysisResults(loadLocal(`project_${projectName}_analysis`, {}));
    }
    loadProjectScriptData(projectName);
    const savedScript = localStorage.getItem(`project_${projectName}_scriptContent`);
    setCreativeBrief(savedScript || '');
    resetPlanState();
    toast({ title: 'Project switched', description: `Switched to project: ${projectName}` });
  };

  const handleCreateProject = async () => {
    const name = window.prompt('Enter new project name:');
    if (!name || !name.trim()) return;
    const trimmedName = name.trim();
    if (projects.includes(trimmedName)) {
      toast({ title: 'Project exists', description: `A project named "${trimmedName}" already exists.`, variant: 'destructive' });
      return;
    }
    if (useDatabase) {
      const newProject = await createProject(trimmedName);
      if (!newProject) return;
      const updated = [...dbProjects, newProject];
      setDbProjects(updated);
      setProjects(updated.map(p => p.name));
      setCurrentProject(newProject.name);
      setCurrentDbProject(newProject);
      setFiles(newProject.files || []);
      setSelectedFiles(new Set());
      setClips(newProject.clips || []);
      setRenderedVideos(newProject.renders || []);
      setSavedRenders(newProject.savedRenders || []);
      setAnalysisResults(newProject.analysisResults || {});
    } else {
      setProjects(prev => [...prev, trimmedName]);
      setCurrentProject(trimmedName);
      setFiles([]);
      setSelectedFiles(new Set());
      setClips([]);
      setRenderedVideos([]);
      setSavedRenders([]);
      setAnalysisResults({});
    }
    resetScriptState();
    setCreativeBrief('');
    localStorage.setItem('currentProject_NLE', trimmedName);
    toast({ title: 'Project created', description: `Created and opened: ${trimmedName}` });
  };

  const handleRenameProject = (oldName: string) => {
    if (useDatabase) {
      toast({ title: 'Rename not available', description: 'Renaming cloud projects is not supported yet.', variant: 'destructive' });
      return;
    }
    if (oldName === 'default') {
      toast({ title: 'Cannot rename default project', variant: 'destructive' });
      return;
    }
    const newName = window.prompt(`Enter new name for project "${oldName}":`, oldName);
    if (!newName || newName.trim() === '' || newName === oldName) return;
    const trimmedName = newName.trim();
    if (projects.includes(trimmedName)) {
      toast({ title: 'Project name already exists', variant: 'destructive' });
      return;
    }
    for (const suffix of ['files', 'clips', 'rendered', 'savedRenders', 'analysis', 'scriptContent', 'scriptFileName', 'scriptAnalysis']) {
      const value = localStorage.getItem(`project_${oldName}_${suffix}`);
      if (value) localStorage.setItem(`project_${trimmedName}_${suffix}`, value);
      localStorage.removeItem(`project_${oldName}_${suffix}`);
    }
    setProjects(prev => prev.map(p => (p === oldName ? trimmedName : p)));
    if (currentProject === oldName) {
      setCurrentProject(trimmedName);
      localStorage.setItem('currentProject_NLE', trimmedName);
    }
    toast({ title: 'Project renamed', description: `"${oldName}" is now "${trimmedName}"` });
  };

  const handleDeleteProject = async (projectName: string) => {
    if (projectName === 'default' && !useDatabase) {
      toast({ title: 'Cannot delete default project', variant: 'destructive' });
      return;
    }
    if (!window.confirm(`Delete project "${projectName}"? This removes all files, clips, and renders.`)) return;
    if (useDatabase) {
      const project = dbProjects.find(p => p.name === projectName);
      if (!project) return;
      const success = await deleteProjectApi(project._id);
      if (!success) {
        toast({ title: 'Delete failed', description: 'Failed to delete project from cloud', variant: 'destructive' });
        return;
      }
      const updated = dbProjects.filter(p => p.name !== projectName);
      setDbProjects(updated);
      setProjects(updated.map(p => p.name));
      if (currentProject === projectName && updated[0]) handleProjectChange(updated[0].name);
    } else {
      for (const suffix of ['files', 'clips', 'rendered', 'savedRenders', 'analysis']) {
        localStorage.removeItem(`project_${projectName}_${suffix}`);
      }
      setProjects(prev => prev.filter(p => p !== projectName));
      if (currentProject === projectName) handleProjectChange('default');
    }
    toast({ title: 'Project deleted', description: `Project "${projectName}" has been deleted` });
  };

  // --- Upload flow ---------------------------------------------------------
  const waitForFileDownload = (file: File): Promise<File> =>
    new Promise((resolve, reject) => {
      const isLikelyICloudPlaceholder = file.size < 1000 && /\.(mov|mp4|m4v)$/i.test(file.name);
      if (!isLikelyICloudPlaceholder) { resolve(file); return; }
      const reader = new FileReader();
      reader.onload = () => resolve(file);
      reader.onerror = () => reject(new Error('Failed to download file from iCloud'));
      reader.readAsArrayBuffer(file);
      setTimeout(() => reject(new Error('iCloud download timeout')), 30000);
    });

  const detectMimeType = (file: File): string => {
    if (file.type && file.type !== 'application/octet-stream') return file.type;
    const ext = file.name.toLowerCase().split('.').pop();
    const map: Record<string, string> = {
      mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', avi: 'video/x-msvideo', webm: 'video/webm',
      mp3: 'audio/mpeg', wav: 'audio/wav', m4a: 'audio/mp4', aac: 'audio/mp4',
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp',
      srt: 'text/plain',
    };
    return map[ext || ''] || file.type || 'application/octet-stream';
  };

  const detectAspectRatio = (width: number, height: number): string => {
    if (!width || !height) return '';
    const ratio = width / height;
    const TOLERANCE = 0.05;
    if (Math.abs(ratio - 16 / 9) < TOLERANCE) return '16:9';
    if (Math.abs(ratio - 9 / 16) < TOLERANCE) return '9:16';
    if (Math.abs(ratio - 1) < TOLERANCE) return '1:1';
    if (Math.abs(ratio - 4 / 5) < TOLERANCE) return '4:5';
    return 'source';
  };

  const addFilesToBin = (newFiles: EnhancerFile[]) => {
    setFiles(prev => [...prev, ...newFiles]);
    setSelectedFiles(prev => {
      const next = new Set(prev);
      newFiles.forEach(f => next.add(f.id));
      return next;
    });
  };

  const onDropMain = async (acceptedFiles: File[], layerType: 'main' | 'background' | 'audio' = 'main') => {
    const videoFiles = acceptedFiles.filter(f => f.type.startsWith('video/') || /\.(mp4|mov|avi|webm|m4v)$/i.test(f.name));
    const audioFiles = acceptedFiles.filter(f => f.type.startsWith('audio/') || /\.(mp3|wav|m4a|aac)$/i.test(f.name));
    const heicFiles = acceptedFiles.filter(f => {
      const ext = f.name.toLowerCase().split('.').pop();
      return ext === 'heic' || ext === 'heif' || f.type === 'image/heic' || f.type === 'image/heif';
    });
    const imageFiles = acceptedFiles.filter(f =>
      !heicFiles.includes(f) && (f.type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(f.name)));
    const srtFiles = acceptedFiles.filter(f => f.name.toLowerCase().endsWith('.srt'));

    if (heicFiles.length > 0) {
      toast({
        title: 'HEIC format not supported',
        description: `${heicFiles.length} HEIC file(s) skipped. Please convert to JPG or PNG first.`,
        variant: 'destructive',
      });
    }

    const allSupported = [...videoFiles, ...audioFiles, ...imageFiles, ...srtFiles];
    if (allSupported.length === 0) {
      toast({ title: 'Invalid files', description: 'Please select video, audio, image, or SRT files', variant: 'destructive' });
      return;
    }

    toast({ title: 'Uploading', description: `Processing ${allSupported.length} file${allSupported.length > 1 ? 's' : ''}…` });

    const newFiles = await Promise.all(allSupported.map(async (file): Promise<EnhancerFile | null> => {
      try {
        const downloadedFile = await waitForFileDownload(file);
        const detectedType = detectMimeType(downloadedFile);

        // Presigned upload (contract: POST /get-upload-url → PUT uploadUrl)
        const response = await fetch('/get-upload-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileName: downloadedFile.name, fileType: detectedType }),
        });
        if (!response.ok) throw new Error('Failed to get upload URL: ' + (await response.text()));
        const { uploadUrl, downloadUrl: origDownloadUrl, s3Key: origS3Key } = await response.json();
        let downloadUrl = origDownloadUrl;
        let s3Key = origS3Key;

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: downloadedFile,
          headers: { 'Content-Type': detectedType },
        });
        if (!uploadResponse.ok) throw new Error(`Failed to upload file to S3: ${uploadResponse.status}`);

        // iPhone MOV / non-MP4 → MediaConvert
        if (detectedType.startsWith('video/') && needsConversion(downloadedFile.name)) {
          toast({ title: 'Converting MOV to MP4…', description: 'This may take a minute' });
          const convResult = await convertMov(s3Key, 'hey-joe-videos');
          s3Key = convResult.outputKey;
          downloadUrl = convResult.downloadUrl;
        }

        const fileType = detectedType.startsWith('video/') ? 'video' :
                         detectedType.startsWith('audio/') ? 'audio' :
                         detectedType.startsWith('image/') ? 'image' : 'srt';

        // Warm the transcript cache in the background so the AI edit can cut on the
        // actual spoken words without hanging the later generate step. Fire-and-forget.
        if (fileType === 'video') {
          fetch('/api/enhancer/warm-transcript', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url: downloadUrl }),
          }).catch(() => {});
        }

        let thumbnailUrl = '';
        let duration = 0;
        let videoWidth = 0;
        let videoHeight = 0;
        let fileAspect = '';

        if (fileType === 'video') {
          const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
          if (isMobile) {
            thumbnailUrl = 'VIDEO_ICON';
            try {
              const videoUrl = URL.createObjectURL(downloadedFile);
              duration = await new Promise<number>((resolve) => {
                const video = document.createElement('video');
                video.muted = true;
                video.playsInline = true;
                const timeout = setTimeout(() => { URL.revokeObjectURL(videoUrl); resolve(0); }, 3000);
                video.onloadedmetadata = () => { clearTimeout(timeout); URL.revokeObjectURL(videoUrl); resolve(video.duration || 0); };
                video.onerror = () => { clearTimeout(timeout); URL.revokeObjectURL(videoUrl); resolve(0); };
                video.src = videoUrl;
              });
            } catch { duration = 0; }
          } else {
            const video = document.createElement('video');
            const canvas = document.createElement('canvas');
            const videoUrl = URL.createObjectURL(downloadedFile);
            thumbnailUrl = await new Promise<string>((resolve) => {
              let resolved = false;
              const cleanup = () => { if (!resolved) { resolved = true; URL.revokeObjectURL(videoUrl); } };
              const timeout = setTimeout(() => { cleanup(); resolve('VIDEO_ICON'); }, 10000);
              video.onloadedmetadata = () => {
                duration = video.duration;
                videoWidth = video.videoWidth;
                videoHeight = video.videoHeight;
                video.currentTime = Math.min(1, video.duration * 0.1);
              };
              video.onseeked = () => {
                const isPortrait = videoHeight > videoWidth;
                canvas.width = isPortrait ? 180 : 320;
                canvas.height = isPortrait ? 320 : 180;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  try {
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    clearTimeout(timeout); cleanup();
                    resolve(canvas.toDataURL('image/jpeg', 0.8));
                    return;
                  } catch { /* fall through */ }
                }
                clearTimeout(timeout); cleanup(); resolve('VIDEO_ICON');
              };
              video.onerror = () => { clearTimeout(timeout); cleanup(); resolve('VIDEO_ICON'); };
              video.src = videoUrl;
            });
            fileAspect = detectAspectRatio(videoWidth, videoHeight);
          }
        } else if (fileType === 'image') {
          thumbnailUrl = downloadUrl; // S3 URL, not blob (prevents expiry)
        } else if (fileType === 'audio') {
          thumbnailUrl = 'AUDIO_ICON';
          const audioUrl = URL.createObjectURL(downloadedFile);
          duration = await new Promise<number>((resolve) => {
            const audio = document.createElement('audio');
            audio.onloadedmetadata = () => { URL.revokeObjectURL(audioUrl); resolve(audio.duration); };
            audio.onerror = () => { URL.revokeObjectURL(audioUrl); resolve(0); };
            audio.src = audioUrl;
          });
        } else if (fileType === 'srt') {
          thumbnailUrl = 'SRT_ICON';
        }

        return {
          id: randId(),
          name: downloadedFile.name,
          fileType,
          thumbnailUrl,
          duration,
          width: videoWidth || undefined,
          height: videoHeight || undefined,
          aspectRatio: fileAspect || undefined,
          timestamp: new Date().toISOString(),
          imported: false,
          s3Url: downloadUrl,
          s3Key,
          layerType,
        };
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        toast({
          title: 'Upload failed',
          description: `Failed to upload ${file.name}. ${error instanceof Error ? error.message : 'Unknown error'}`,
          variant: 'destructive',
        });
        return null;
      }
    }));

    const successfulFiles = newFiles.filter((f): f is EnhancerFile => f !== null);
    if (successfulFiles.length > 0) {
      addFilesToBin(successfulFiles);
      toast({ title: 'Files uploaded', description: `${successfulFiles.length} file${successfulFiles.length > 1 ? 's' : ''} uploaded successfully` });

      // Auto-analyze uploaded videos (transcripts)
      const videos = successfulFiles.filter(f => f.fileType === 'video' && f.s3Url);
      if (videos.length > 0) {
        handleAnalyze(videos);
        const detected = videos.find(f => f.aspectRatio)?.aspectRatio;
        if (detected) setAspectRatio(detected);
      }
    } else if (newFiles.length > 0) {
      toast({ title: 'Upload failed', description: 'All files failed to upload. Please try again.', variant: 'destructive' });
    }
  };

  // Audio files auto-route to the audio layer; everything else goes to main.
  const routeDrop = (dropped: File[]) => {
    const audio = dropped.filter(f => f.type.startsWith('audio/') || /\.(mp3|wav|m4a|aac)$/i.test(f.name));
    const other = dropped.filter(f => !audio.includes(f));
    if (audio.length > 0) onDropMain(audio, 'audio');
    if (other.length > 0) onDropMain(other, 'main');
  };

  const dropzoneAccept = {
    'video/*': ['.mp4', '.mov', '.avi', '.webm', '.m4v'],
    'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    'text/plain': ['.srt'],
    'audio/*': ['.mp3', '.wav', '.m4a', '.aac'],
    'application/octet-stream': ['.mp4', '.mov', '.m4v'], // iOS fallback
  };

  const mainDropzone = useDropzone({ onDrop: routeDrop, accept: dropzoneAccept, multiple: true, preventDropOnDocument: true });
  const headerDropzone = useDropzone({ onDrop: routeDrop, accept: dropzoneAccept, multiple: true, preventDropOnDocument: true, noDrag: true });

  // --- Library picker ------------------------------------------------------
  const handleLibraryPick = async (staged: StagedLibraryFile) => {
    const name = stagedFileName(staged);
    toast({ title: 'Adding from library…', description: name });
    try {
      let s3Key = staged.key;
      let url = staged.downloadUrl;
      if (needsConversion(name)) {
        toast({ title: 'Converting to MP4…', description: 'This may take a minute' });
        const conv = await convertMov(staged.key); // staged files live in the tool bucket (convert-mov default)
        s3Key = conv.outputKey;
        url = conv.downloadUrl;
      }
      let meta = { duration: 0, width: 0, height: 0 };
      try { meta = await probeVideoUrl(url); } catch { /* metadata probe is best-effort */ }
      const newFile: EnhancerFile = {
        id: randId(),
        name,
        fileType: 'video',
        thumbnailUrl: '',
        duration: meta.duration,
        width: meta.width || undefined,
        height: meta.height || undefined,
        aspectRatio: detectAspectRatio(meta.width, meta.height) || undefined,
        timestamp: new Date().toISOString(),
        imported: false,
        s3Url: url,
        s3Key,
        layerType: 'main',
      };
      addFilesToBin([newFile]);
      handleAnalyze([newFile]);
      if (newFile.aspectRatio) setAspectRatio(newFile.aspectRatio);
      toast({ title: 'Added from library', description: `"${name}" is in your assets` });
    } catch (error: any) {
      toast({ title: 'Could not add from library', description: error.message || 'Unknown error', variant: 'destructive' });
    }
  };

  // --- Casting import ------------------------------------------------------
  const handleVideosImported = (importedVideos: any[]) => {
    const projectName = importedVideos[0]?.castingData?.projectName || importedVideos[0]?.projectName || 'Untitled Project';
    const sessionName = importedVideos[0]?.castingData?.sessionName || importedVideos[0]?.sessionName || 'Session';
    setPendingImportData({ videos: importedVideos, projectName: `${projectName} - ${sessionName}` });
    setShowImportChoice(true);
    setShowImporter(false);
  };

  const processImport = async (createNewProject: boolean) => {
    if (!pendingImportData) return;
    const { videos: importedVideos, projectName: fullProjectName } = pendingImportData;

    if (createNewProject) {
      if (useDatabase) {
        const newProject = await createProject(fullProjectName);
        if (!newProject) {
          toast({
            title: 'Project creation failed',
            description: `Could not create "${fullProjectName}". Please create a project first, then import again.`,
            variant: 'destructive',
          });
          setPendingImportData(null);
          setShowImportChoice(false);
          return;
        }
        setDbProjects(prev => [...prev, newProject]);
        setProjects(prev => (prev.includes(fullProjectName) ? prev : [...prev, fullProjectName]));
        setCurrentProject(fullProjectName);
        setCurrentDbProject(newProject);
      } else {
        setProjects(prev => (prev.includes(fullProjectName) ? prev : [...prev, fullProjectName]));
        setCurrentProject(fullProjectName);
      }
      localStorage.setItem('currentProject_NLE', fullProjectName);
      setFiles([]);
      setSelectedFiles(new Set());
      setClips([]);
      setRenderedVideos([]);
      setSavedRenders([]);
      setAnalysisResults({});
      resetScriptState();
      setCreativeBrief('');
    }

    const newFiles: EnhancerFile[] = importedVideos.map(video => ({
      id: video.id || randId(),
      name: video.name || video.fileName || 'Imported Video',
      fileType: 'video' as const,
      thumbnailUrl: video.thumbnailUrl || video.castingData?.thumbnailUrl,
      duration: video.duration || 0,
      timestamp: new Date().toISOString(),
      projectName: video.castingData?.projectName || video.projectName,
      sessionName: video.castingData?.sessionName || video.sessionName,
      groupName: video.castingData?.groupName || video.groupName,
      takeName: video.castingData?.takeName || video.takeName,
      talent: video.castingData?.talent || video.talent,
      s3Url: video.s3Url,
      s3Key: video.s3Key,
      imported: true,
      liked: video.castingData?.liked || video.liked,
      comments: video.castingData?.comments || video.comments,
    }));

    setFiles(prev => {
      const existingKeys = new Set(prev.map(f => f.s3Key));
      const uniqueNewFiles = newFiles.filter(f => !existingKeys.has(f.s3Key));
      setSelectedFiles(prevSel => {
        const next = new Set(prevSel);
        uniqueNewFiles.forEach(f => next.add(f.id));
        return next;
      });
      return [...prev, ...uniqueNewFiles];
    });

    setPendingImportData(null);
    setShowImportChoice(false);
    toast({
      title: 'Videos imported',
      description: `${newFiles.length} video${newFiles.length > 1 ? 's' : ''} imported to ${createNewProject ? fullProjectName : currentProject}`,
    });

    const videos = newFiles.filter(f => f.fileType === 'video' && f.s3Url);
    if (videos.length > 0) handleAnalyze(videos);
  };

  // --- AI asset generation -------------------------------------------------
  const executeAIGenerate = async (
    type: 'image' | 'video' | 'music',
    prompt: string,
    options: { aspectRatio?: string; loop?: boolean; duration?: number; intensity?: string; format?: string; model?: 'luma' | 'runway' },
  ) => {
    try {
      let endpoint: string;
      let body: Record<string, unknown>;
      if (type === 'image') {
        endpoint = '/api/generate-image';
        body = { prompt, aspectRatio: options.aspectRatio };
      } else if (type === 'video') {
        endpoint = '/api/generate-video';
        body = { prompt, aspectRatio: options.aspectRatio, loop: options.loop, model: options.model, duration: options.duration };
      } else {
        endpoint = '/api/generate-music';
        body = { prompt, duration: options.duration || 30, intensity: options.intensity || 'medium', format: options.format || 'mp3' };
      }
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to start generation');
      }
      const data = await response.json();
      setAiGeneratingJob({ jobId: data.jobId, type, status: 'processing', progress: 0 });
      toast({
        title: `${type === 'image' ? 'Image' : type === 'video' ? 'Video' : 'Music'} generation started`,
        description: type === 'video' ? 'This may take 1-3 minutes…' : type === 'music' ? 'This typically takes 30-60 seconds…' : 'This typically takes 10-20 seconds…',
      });
      pollAIGenerationStatus(data.jobId, type, prompt, options.duration, options.model === 'runway' ? `Runway - ${prompt.substring(0, 30)}...` : undefined);
    } catch (error: any) {
      toast({ title: 'Generation failed', description: error.message || 'Failed to start AI generation', variant: 'destructive' });
      setAiGeneratingJob(null);
    }
  };

  const handleAIGenerate = (
    type: 'image' | 'video' | 'music',
    prompt: string,
    options: { aspectRatio?: string; loop?: boolean; duration?: number; intensity?: string; format?: string; model?: 'luma' | 'runway' },
  ) => {
    const operationMap: Record<string, { operation: CreditOperation; name: string }> = {
      image: { operation: CREDIT_OPERATIONS.IMAGE_GENERATION, name: 'Image Generation' },
      video: { operation: CREDIT_OPERATIONS.VIDEO_GENERATION, name: 'Video Generation' },
      music: { operation: CREDIT_OPERATIONS.MUSIC_GENERATION, name: 'Music Generation' },
    };
    const { operation, name } = operationMap[type];
    requireCreditConfirmation(operation, name, () => executeAIGenerate(type, prompt, options));
  };

  const pollAIGenerationStatus = (jobId: string, type: 'image' | 'video' | 'music', prompt: string, duration?: number, fileName?: string) => {
    const maxAttempts = type === 'video' ? 180 : type === 'music' ? 90 : 60;
    let attempts = 0;
    const poll = async () => {
      try {
        const response = await fetch(`/api/generate-asset/status/${jobId}`);
        const data = await response.json();
        if (data.status === 'completed' && data.assetUrl) {
          const typeLabel = type === 'image' ? 'Image' : type === 'video' ? 'Video' : 'Music';
          const fileType = type === 'video' ? 'video' : type === 'music' ? 'audio' : 'image';
          const newFile: EnhancerFile = {
            id: `ai-${jobId}`,
            name: fileName || `AI ${typeLabel} - ${prompt.substring(0, 30)}...`,
            fileType: fileType as 'video' | 'image' | 'audio',
            s3Url: data.assetUrl,
            s3Key: data.s3Key,
            duration: type === 'video' ? (duration || 5) : type === 'music' ? (duration || data.duration || 30) : 0,
            thumbnailUrl: type === 'image' ? data.assetUrl : undefined,
            isAIGenerated: true,
            aiGenerationPrompt: prompt,
            aiAssetType: type,
            width: type === 'image' ? (data.width || 1024) : undefined,
            height: type === 'image' ? (data.height || 1024) : undefined,
          };
          addFilesToBin([newFile]);
          setAiGeneratingJob({ jobId, type, status: 'completed', progress: 100, assetUrl: data.assetUrl });
          toast({
            title: `${typeLabel} generated!`,
            description: type === 'music'
              ? 'Your AI music has been added to your assets. Include it in the edit or add it to the timeline.'
              : 'Your AI asset has been added to your assets.',
          });
          setTimeout(() => setAiGeneratingJob(null), 3000);
          return;
        }
        if (data.status === 'failed') {
          setAiGeneratingJob({ jobId, type, status: 'failed', progress: 0 });
          toast({ title: 'Generation failed', description: data.error || 'AI generation failed. Please try again.', variant: 'destructive' });
          return;
        }
        setAiGeneratingJob({ jobId, type, status: data.status, progress: data.progress || 0 });
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          setAiGeneratingJob({ jobId, type, status: 'failed', progress: 0 });
          toast({ title: 'Generation timeout', description: 'The generation is taking too long. Please try again.', variant: 'destructive' });
        }
      } catch {
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 5000);
      }
    };
    setTimeout(poll, 2000);
  };

  // --- Analysis (transcription) --------------------------------------------
  const handleAnalyze = async (fileIdsOrFiles: string[] | EnhancerFile[]) => {
    if (fileIdsOrFiles.length === 0) return;
    const isFileObjects = typeof fileIdsOrFiles[0] === 'object';
    const analyzableFiles = (isFileObjects
      ? (fileIdsOrFiles as EnhancerFile[])
      : files.filter(f => (fileIdsOrFiles as string[]).includes(f.id))
    ).filter(f => f.fileType === 'video' || (f.fileType === 'audio' && f.linkedVideoId));

    if (analyzableFiles.length === 0) {
      toast({ title: 'No analyzable files', description: 'Select video files for analysis', variant: 'destructive' });
      return;
    }

    const analysisJobs: string[] = [];
    try {
      for (const file of analyzableFiles) {
        if (!file.s3Url) continue;
        const response = await fetch('/api/analyze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            video_url: file.s3Url,
            video_id: file.id,
            modules: ['transcribe', 'background', 'visual'],
          }),
        });
        if (response.ok) {
          const result = await response.json();
          analysisJobs.push(result.analysis_id);
          setFiles(prev => prev.map(f =>
            f.id === file.id ? { ...f, analysisStatus: 'starting' as const, analysisJobId: result.analysis_id } : f));
        } else {
          setFiles(prev => prev.map(f => (f.id === file.id ? { ...f, analysisStatus: 'failed' as const } : f)));
        }
      }
      if (analysisJobs.length > 0) {
        toast({ title: 'Analysis started', description: `Transcribing ${analysisJobs.length} video${analysisJobs.length > 1 ? 's' : ''}…` });
        pollAnalysisResults(analysisJobs);
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast({ title: 'Analysis failed', description: 'Failed to start video analysis', variant: 'destructive' });
    }
  };

  const pollAnalysisResults = (analysisIds: string[]) => {
    const pollInterval = setInterval(async () => {
      let allCompleted = true;
      for (const analysisId of analysisIds) {
        if (analysisResultsRef.current[analysisId]?.status === 'completed') continue;
        try {
          const response = await fetch(`/api/analyze/status/${analysisId}`);
          if (!response.ok) continue;
          const result = await response.json();
          setAnalysisResults(prev => ({ ...prev, [analysisId]: result }));
          if (result.status === 'completed') {
            setFiles(prev => prev.map(f =>
              f.analysisJobId === analysisId
                ? { ...f, analysisStatus: 'completed' as const, analysisJobId: undefined, analysisData: result.results }
                : f));
            toast({ title: 'Analysis complete', description: 'Video transcription finished' });
          } else if (result.status === 'failed') {
            setFiles(prev => prev.map(f =>
              f.analysisJobId === analysisId ? { ...f, analysisStatus: 'failed' as const, analysisJobId: undefined } : f));
          } else {
            if (result.status === 'processing') {
              setFiles(prev => prev.map(f =>
                f.analysisJobId === analysisId ? { ...f, analysisStatus: 'processing' as const } : f));
            }
            allCompleted = false;
          }
        } catch {
          setFiles(prev => prev.map(f =>
            f.analysisJobId === analysisId ? { ...f, analysisStatus: 'failed' as const, analysisJobId: undefined } : f));
        }
      }
      if (allCompleted) clearInterval(pollInterval);
    }, 3000);

    // Analysis can be slow — hard stop after 30 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      for (const analysisId of analysisIds) {
        if (analysisResultsRef.current[analysisId]?.status !== 'completed') {
          setFiles(prev => prev.map(f =>
            f.analysisJobId === analysisId ? { ...f, analysisStatus: 'failed' as const, analysisJobId: undefined } : f));
        }
      }
    }, 1800000);
  };

  // --- Selection -----------------------------------------------------------
  const handleToggleSelect = (fileId: string) => {
    setSelectedFiles(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };
  const handleSelectAll = () => setSelectedFiles(new Set(files.map(f => f.id)));
  const handleDeselectAll = () => setSelectedFiles(new Set());

  // --- Timeline clip management (ported) ------------------------------------
  const handleClipDrop = (fileId: string) => {
    const file = files.find(f => f.id === fileId);
    if (!file) return;
    const newClips: ProjectClip[] = [];

    let clipPrompt = '';
    if (file.enhanced) clipPrompt = '';
    else if (file.layerType === 'background') clipPrompt = 'background';
    else if (file.layerType === 'audio') clipPrompt = file.linkedVideoId ? 'Original audio track from green screen video' : 'Use this audio track';
    else if (file.fileType === 'video') clipPrompt = file.hasGreenScreen ? 'chromaKey #97eca9' : '';
    else if (file.fileType === 'audio') clipPrompt = 'Use this audio track';
    else if (file.fileType === 'image') clipPrompt = 'Use this image as background or overlay';
    else if (file.fileType === 'srt') clipPrompt = 'Use this subtitle track';

    let clipLayerType = file.layerType;
    if (file.fileType === 'image' && !clipLayerType) clipLayerType = 'main';

    const newClip: ProjectClip = {
      id: randId(),
      sourceFileId: file.id,
      position: clips.length,
      prompt: clipPrompt,
      duration: file.duration,
      name: file.hasGreenScreen ? `${file.name} (Green Screen)` : file.name,
      thumbnailUrl: file.thumbnailUrl,
      fileType: file.fileType,
      analysisData: file.analysisData,
      layerType: clipLayerType as any,
      linkedPairId: file.linkedPairId,
      linkedMaskId: file.linkedMaskId,
      isLinked: !!(file.linkedAudioId || file.linkedVideoId),
      inTime: 0,
      outTime: file.duration || undefined,
      s3Url: file.greenScreenUrl || file.s3Url,
      s3Key: file.s3Key || file.greenScreenKey,
      imageMode: file.fileType === 'image' ? 'fullframe' : undefined,
      imageDuration: file.fileType === 'image' ? 5 : undefined,
    } as ProjectClip;
    newClips.push(newClip);

    if (file.linkedMaskId) {
      const maskFile = files.find(f => f.id === file.linkedMaskId);
      if (maskFile) {
        newClips.push({
          id: randId(),
          sourceFileId: maskFile.id,
          position: clips.length + 1,
          prompt: 'luma matte for compositing',
          duration: maskFile.duration,
          name: maskFile.displayName || maskFile.name,
          thumbnailUrl: maskFile.thumbnailUrl,
          fileType: maskFile.fileType,
          layerType: 'mask' as any,
          linkedPairId: file.linkedPairId,
          isLinked: true,
        } as ProjectClip);
      }
    }

    if (file.layerType === 'mask' && file.linkedVideoId) {
      const originalFile = files.find(f => f.id === file.linkedVideoId);
      if (originalFile && !clips.some(c => c.sourceFileId === originalFile.id)) {
        newClips.unshift({
          id: randId(),
          sourceFileId: originalFile.id,
          position: clips.length,
          prompt: '',
          duration: originalFile.duration,
          name: originalFile.displayName || originalFile.name,
          thumbnailUrl: originalFile.thumbnailUrl,
          fileType: originalFile.fileType,
          layerType: 'main' as any,
          linkedPairId: file.linkedPairId,
          isLinked: true,
        } as ProjectClip);
      }
    }

    setClips(prev => [...prev, ...newClips]);
    toast({
      title: 'Clips added',
      description: newClips.length > 1 ? `${file.name} and linked media added to timeline` : `${file.name} added to timeline`,
    });
  };

  const handleClipDropWithOptions = (fileId: string, options: { includeMask?: boolean; videoOnly?: boolean }) => {
    const file = files.find(f => f.id === fileId);
    if (!file) return;
    const newClips: ProjectClip[] = [];
    newClips.push({
      id: randId(),
      sourceFileId: file.id,
      position: clips.length,
      prompt: '',
      duration: file.duration,
      name: file.displayName || file.name,
      thumbnailUrl: file.thumbnailUrl,
      fileType: file.fileType,
      analysisData: file.analysisData,
      layerType: 'main' as any,
      linkedPairId: file.linkedPairId,
      linkedMaskId: file.linkedMaskId,
      isLinked: false,
      inTime: 0,
      outTime: file.duration || undefined,
      s3Url: file.greenScreenUrl || file.s3Url,
      s3Key: file.s3Key || file.greenScreenKey,
    } as ProjectClip);

    if (options.includeMask && file.linkedMaskId) {
      const maskFile = files.find(f => f.id === file.linkedMaskId);
      if (maskFile) {
        newClips.push({
          id: randId(),
          sourceFileId: maskFile.id,
          position: clips.length + 1,
          prompt: 'luma matte for compositing',
          duration: maskFile.duration,
          name: maskFile.displayName || maskFile.name,
          thumbnailUrl: maskFile.thumbnailUrl,
          fileType: maskFile.fileType,
          layerType: 'mask' as any,
          linkedPairId: file.linkedPairId,
          isLinked: true,
        } as ProjectClip);
      }
    }
    setClips(prev => [...prev, ...newClips]);
    toast({
      title: 'Clips added',
      description: options.includeMask && newClips.length > 1
        ? `${file.name} with luma matte added to timeline`
        : `${file.name} added to timeline (video only)`,
    });
  };

  const handleAddSelectedToTimeline = () => {
    files.filter(f => selectedFiles.has(f.id) && f.layerType !== 'mask').forEach(f => handleClipDrop(f.id));
  };

  const handleClipRemove = (clipId: string) => setClips(prev => prev.filter(c => c.id !== clipId));

  const handlePreviewClip = (clipId: string) => {
    const clip = clips.find(c => c.id === clipId);
    if (!clip) return;
    const file = files.find(f => f.id === clip.sourceFileId);
    if (!file) return;
    setPreviewClip({ clip, file });
  };

  const handleFileRemove = (fileId: string) => {
    if (!window.confirm('Remove this file from your assets?')) return;
    setFiles(prev => prev.filter(f => f.id !== fileId));
    setSelectedFiles(prev => {
      const next = new Set(prev);
      next.delete(fileId);
      return next;
    });
    // FIX (legacy bug): clips reference files via sourceFileId, not fileId
    setClips(prev => prev.filter(c => c.sourceFileId !== fileId));
    toast({ title: 'File removed', description: 'File has been removed from your assets' });
  };

  const handleClipReorder = (clipId: string, newPosition: number) => {
    setClips(prev => {
      const clipIndex = prev.findIndex(c => c.id === clipId);
      if (clipIndex === -1) return prev;
      const newClips = [...prev];
      const [removed] = newClips.splice(clipIndex, 1);
      newClips.splice(newPosition, 0, removed);
      return newClips.map((clip, index) => ({ ...clip, position: index }));
    });
  };

  const patchClip = (clipId: string, updates: Partial<ProjectClip>) =>
    setClips(prev => prev.map(clip => (clip.id === clipId ? { ...clip, ...updates } : clip)));

  const handlePromptChange = (clipId: string, prompt: string) => patchClip(clipId, { prompt });
  const handleTimeChange = (clipId: string, inTime: number | undefined, outTime: number | undefined) => {
    setClips(prev => prev.map(clip => {
      if (clip.id !== clipId) return clip;
      const updated = { ...clip, inTime, outTime };
      if (inTime !== undefined && outTime !== undefined) updated.duration = parseFloat((outTime - inTime).toFixed(3));
      return updated;
    }));
  };
  const handleTitleChange = (clipId: string, titleText?: string, titlePosition?: any, titleFont?: string, titleColor?: string) =>
    patchClip(clipId, { titleText, titlePosition, titleFont, titleColor } as any);
  const handleVolumeChange = (clipId: string, volume: number) => patchClip(clipId, { volume } as any);
  const handleTransitionChange = (clipId: string, transitionOut: any) => patchClip(clipId, { transitionOut } as any);
  const handleEffectChange = (clipId: string, effect: any) => patchClip(clipId, { effect: effect === 'none' ? undefined : effect } as any);
  const handleImageSettingsChange = (clipId: string, imageMode: any, imagePosition: any, imageDuration: number, imageScale: number) =>
    patchClip(clipId, { imageMode, imagePosition, imageDuration, imageScale } as any);
  const handleCaptionsChange = (clipId: string, showCaptions: boolean, captionStyle: any, captionFont?: string, captionBackground?: string, captionFontColor?: string, captionStrokeColor?: string) =>
    patchClip(clipId, { showCaptions, captionStyle, captionFont, captionBackground, captionFontColor, captionStrokeColor } as any);
  const handleReferenceTextChange = (clipId: string, referenceText: string) => patchClip(clipId, { referenceText } as any);
  const handleLayerTypeChange = (clipId: string, layerType: 'main' | 'background') => patchClip(clipId, { layerType } as any);

  const handleStackClip = (draggedClipId: string, targetClipId: string) => {
    setClips(prev => {
      const draggedClip = prev.find(c => c.id === draggedClipId);
      const targetClip = prev.find(c => c.id === targetClipId);
      if (!draggedClip || !targetClip) return prev;
      if ((targetClip as any).stackedOn === draggedClipId) return prev;
      const targetTrack = (targetClip as any).track || 0;
      const existingOverlays = prev.filter(c => (c as any).stackedOn === targetClipId);
      const maxOverlayTrack = existingOverlays.reduce((max, c) => Math.max(max, (c as any).track || 0), targetTrack);
      return prev.map(clip => clip.id === draggedClipId ? {
        ...clip,
        isOverlay: true,
        track: maxOverlayTrack + 1,
        stackedOn: targetClipId,
        overlayPosition: 'center' as const,
        overlayScale: 1.0,
        volume: 0,
        inTime: clip.inTime !== undefined ? clip.inTime : 0,
        outTime: clip.outTime !== undefined ? clip.outTime : clip.duration,
      } as any : clip);
    });
    toast({ title: 'Clip stacked', description: 'Clip stacked as an overlay. Adjust position and scale in overlay settings.' });
  };

  const handleUnstackClip = (clipId: string) => {
    patchClip(clipId, { isOverlay: false, track: 0, stackedOn: undefined, overlayPosition: undefined, overlayScale: undefined, volume: 1.0 } as any);
    toast({ title: 'Clip unstacked', description: 'Clip moved back to the main track.' });
  };

  const handleOverlaySettingsChange = (clipId: string, position: any, scale: number) =>
    patchClip(clipId, { overlayPosition: position, overlayScale: position === 'center' ? 1.0 : scale } as any);

  const handleInsertText = (text: string, file?: EnhancerFile, startTime?: number, endTime?: number, referenceText?: string) => {
    if (file && startTime !== undefined && endTime !== undefined) {
      const segmentDuration = endTime - startTime;
      const newClips: ProjectClip[] = [];
      if (file.fileType === 'audio' && file.linkedVideoId) {
        const linkedVideo = files.find(f => f.id === file.linkedVideoId);
        if (linkedVideo) {
          newClips.push({
            id: randId(),
            name: `${linkedVideo.name} (${startTime.toFixed(1)}s-${endTime.toFixed(1)}s)`,
            fileType: 'video',
            thumbnailUrl: linkedVideo.thumbnailUrl || '',
            duration: segmentDuration,
            sourceFileId: linkedVideo.id,
            prompt: text,
            referenceText,
            analysisData: linkedVideo.analysisData,
            layerType: 'main' as any,
            linkedPairId: file.linkedPairId,
            isLinked: true,
            position: clips.length,
            inTime: startTime,
            outTime: endTime,
          } as ProjectClip);
          newClips.push({
            id: randId(),
            name: `${file.name} (${startTime.toFixed(1)}s-${endTime.toFixed(1)}s)`,
            fileType: 'audio',
            thumbnailUrl: file.thumbnailUrl || 'AUDIO_ICON',
            duration: segmentDuration,
            sourceFileId: file.id,
            prompt: text,
            referenceText,
            analysisData: file.analysisData,
            layerType: 'audio' as any,
            linkedPairId: file.linkedPairId,
            isLinked: true,
            position: clips.length + 1,
            inTime: startTime,
            outTime: endTime,
          } as ProjectClip);
        }
      } else {
        newClips.push({
          id: Date.now().toString(),
          name: `${file.name} (${startTime.toFixed(1)}s-${endTime.toFixed(1)}s)`,
          fileType: file.fileType || 'video',
          thumbnailUrl: file.thumbnailUrl || '',
          duration: segmentDuration,
          sourceFileId: file.id,
          prompt: text,
          referenceText,
          analysisData: file.analysisData,
          inTime: startTime,
          outTime: endTime,
        } as ProjectClip);
      }
      setClips(prev => [...prev, ...newClips]);
      toast({
        title: newClips.length > 1 ? 'Video and audio segments added to timeline' : 'Video segment added to timeline',
        description: `${segmentDuration.toFixed(1)}s clip: "${text.substring(0, 40)}${text.length > 40 ? '...' : ''}"`,
      });
    } else {
      const textClip = {
        id: Date.now().toString(),
        name: `Text: ${text.substring(0, 30)}${text.length > 30 ? '...' : ''}`,
        fileType: 'text',
        thumbnailUrl: '',
        duration: 5,
        sourceFileId: '',
        prompt: text,
      } as unknown as ProjectClip;
      setClips(prev => [...prev, textClip]);
      toast({ title: 'Text added to timeline', description: `"${text.substring(0, 50)}${text.length > 50 ? '...' : ''}"` });
    }
  };

  // Transcript edits on the source file persist to analysisData (used by
  // captions and the AI pipeline).
  const handleTranscriptEdit = (fileId: string, segmentIndex: number, newText: string) => {
    setFiles(prev => prev.map(file => {
      if (file.id !== fileId) return file;
      const updatedAnalysisData: any = file.analysisData ? { ...file.analysisData } : { transcript: { segments: [] } };
      if (updatedAnalysisData.transcript?.segments?.[segmentIndex]) {
        const updatedSegments = [...updatedAnalysisData.transcript.segments];
        updatedSegments[segmentIndex] = { ...updatedSegments[segmentIndex], text: newText };
        updatedAnalysisData.transcript = { ...updatedAnalysisData.transcript, segments: updatedSegments };
      }
      return { ...file, analysisData: updatedAnalysisData };
    }));
  };

  const handleSpeakerEdit = (fileId: string, segmentIndex: number, newSpeaker: string) => {
    setFiles(prev => prev.map(file => {
      if (file.id !== fileId) return file;
      const updatedAnalysisData: any = file.analysisData ? { ...file.analysisData } : { transcript: { segments: [] } };
      if (updatedAnalysisData.transcript?.segments?.[segmentIndex]) {
        const updatedSegments = [...updatedAnalysisData.transcript.segments];
        updatedSegments[segmentIndex] = { ...updatedSegments[segmentIndex], speaker: newSpeaker };
        updatedAnalysisData.transcript = { ...updatedAnalysisData.transcript, segments: updatedSegments };
      }
      return { ...file, analysisData: updatedAnalysisData };
    }));
  };

  // --- Master prompt --------------------------------------------------------
  function generateMasterPrompt(): string {
    const videoClips = clips.filter(c =>
      c.fileType === 'video' && c.layerType !== 'mask' &&
      !c.name?.includes('Luma Matte') && !c.name?.includes('foreground_mask') && !c.name?.includes('_mask'));
    const lumaMatteClips = clips.filter(c =>
      c.fileType === 'video' && (c.layerType === 'mask' ||
        c.name?.includes('Luma Matte') || c.name?.includes('foreground_mask') || c.name?.includes('_mask')));
    const audioClips = clips.filter(c => c.fileType === 'audio');
    const imageClips = clips.filter(c => c.fileType === 'image');

    const simplifyName = (name: string) => name.replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim();
    const clipNames = videoClips.map(clip => `"${simplifyName(clip.name)}"`);

    let prompt = '';
    if (scriptContent) {
      prompt = `SCRIPT/CREATIVE DIRECTION:\n${scriptContent}\n\nEdit the following clips to match this script. Use the transcript timings in each clip's comment to select the right portions. `;
    }
    if (clipNames.length > 0) {
      if (!scriptContent) {
        prompt = `Generate video that edits these ${clipNames.length} clips together in sequence: ${clipNames.join(', ')}. `;
      } else {
        prompt += `Available clips: ${clipNames.join(', ')}. `;
      }
    }
    const allHaveGreenScreen = videoClips.length > 0 && videoClips.every(c => c.prompt?.includes('chromaKey'));
    if (allHaveGreenScreen) prompt += 'Apply chromaKey to remove green background from all videos. ';
    audioClips.forEach(clip => { prompt += `Add "${simplifyName(clip.name)}" as audio track. `; });
    const backgroundImages = imageClips.filter(clip => clip.layerType === 'background' || clip.prompt?.includes('background'));
    if (backgroundImages.length > 0) prompt += `Use "${simplifyName(backgroundImages[0].name)}" as background. `;
    lumaMatteClips.forEach(clip => { prompt += `Apply luma matte "${simplifyName(clip.name)}" for transparency. `; });
    return prompt.trim() || 'Create video from provided clips';
  }

  // --- Quick preview ---------------------------------------------------------
  const buildQuickPreview = (sourceClips: ProjectClip[]): QuickPreview | null => {
    const videoClips = sourceClips.filter(c =>
      c.fileType === 'video' && c.layerType !== 'mask' &&
      !c.name?.includes('Luma Matte') && !c.name?.includes('foreground_mask'));
    const imageClips = sourceClips.filter(c => c.fileType === 'image' && c.layerType !== 'background');
    const audioClips = sourceClips.filter(c => c.fileType === 'audio');

    const previewClips: QuickPreview['clips'] = [];
    let totalDuration = 0;

    videoClips.forEach((clip, index) => {
      const file = files.find(f => f.id === clip.sourceFileId);
      const inTime = clip.inTime || 0;
      const outTime = clip.outTime || clip.duration || file?.duration || 5;
      const clipDuration = outTime - inTime;
      const effects: string[] = [];
      if ((clip as any).transitionOut) effects.push(`Transition: ${(clip as any).transitionOut}`);
      if ((clip as any).volume !== undefined && (clip as any).volume !== 1) effects.push(`Volume: ${Math.round((clip as any).volume * 100)}%`);
      if ((clip as any).titleText) effects.push(`Title: "${(clip as any).titleText}"`);
      const url = resolveVideoUrl(file?.s3Url, file?.s3Key) || (file as any)?.url || (clip as any).url || '';
      if (url) {
        previewClips.push({
          id: clip.id,
          type: 'video',
          name: (clip.name || `Clip ${index + 1}`).replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim(),
          url,
          inTime,
          outTime,
          duration: clipDuration,
          thumbnailUrl: clip.thumbnailUrl,
          effects,
        });
        totalDuration += clipDuration;
      }
    });

    imageClips.forEach((clip, index) => {
      const file = files.find(f => f.id === clip.sourceFileId);
      const imageDuration = (clip as any).imageDuration || clip.duration || 5;
      const url = resolveVideoUrl(file?.s3Url, file?.s3Key) || (file as any)?.url || (clip as any).url || clip.thumbnailUrl || '';
      if (url) {
        previewClips.push({
          id: clip.id,
          type: 'image',
          name: (clip.name || `Image ${index + 1}`).replace(/\s*\([\d.]+s-[\d.]+s\)\s*/g, '').trim(),
          url,
          inTime: 0,
          outTime: imageDuration,
          duration: imageDuration,
          thumbnailUrl: clip.thumbnailUrl || url,
          effects: [],
        });
        totalDuration += imageDuration;
      }
    });

    if (previewClips.length === 0) return null;
    return {
      clips: previewClips,
      summary: {
        totalClips: previewClips.length,
        totalDuration: `${totalDuration.toFixed(1)}s`,
        videoClips: videoClips.length,
        imageClips: imageClips.length,
        audioTracks: audioClips.length,
      },
      totalDuration,
    };
  };

  const handleQuickPreview = (sourceClips?: ProjectClip[]) => {
    const qp = buildQuickPreview(sourceClips || clips);
    if (!qp) return;
    setQuickPreview(qp);
    setShowQuickPreviewPanel(true);
  };

  // --- AI edit generation (the /api/shotstack/preview → timeline path) ------
  const convertShotstackToTimelineClips = (shotstackJson: any): ProjectClip[] => {
    const tracks = shotstackJson?.timeline?.tracks || [];
    const videoClips: any[] = [];
    const textOverlays: Array<{ text: string; start: number; length: number; position: string }> = [];

    for (const track of tracks) {
      for (const clip of (track.clips || [])) {
        if (clip.asset?.type === 'video') videoClips.push(clip);
        else if (clip.asset?.type === 'text') {
          textOverlays.push({
            text: clip.asset.text || '',
            start: clip.start || 0,
            length: clip.length || 3,
            position: clip.position || 'center',
          });
        }
      }
    }

    videoClips.sort((a, b) => (a.start || 0) - (b.start || 0));

    let cumulativeStart = 0;
    const clipTimings: Array<{ start: number; end: number }> = [];
    videoClips.forEach(clip => {
      const clipLength = clip.length || 5;
      clipTimings.push({ start: cumulativeStart, end: cumulativeStart + clipLength });
      cumulativeStart += clipLength;
    });

    return videoClips.map((clip, index) => {
      const srcUrl = clip.asset?.src || '';
      const matchingFile = files.find(f =>
        f.s3Url === srcUrl || (f.s3Key && srcUrl.includes(f.s3Key)) || srcUrl.includes(f.name));
      const trimStart = clip.asset?.trim || 0;
      const clipLength = clip.length || 5;

      let transitionOut: any = 'none';
      const transitionType = clip.transition?.out || clip.transition?.in || '';
      if (transitionType.includes('fade')) transitionOut = transitionType.includes('Slow') ? 'fadeSlow' : transitionType.includes('Fast') ? 'fadeFast' : 'fade';
      else if (transitionType.includes('wipeLeft')) transitionOut = 'wipeLeft';
      else if (transitionType.includes('wipeRight')) transitionOut = 'wipeRight';
      else if (transitionType.includes('slideLeft')) transitionOut = 'slideLeft';
      else if (transitionType.includes('slideRight')) transitionOut = 'slideRight';
      else if (transitionType.includes('slideUp')) transitionOut = 'slideUp';
      else if (transitionType.includes('slideDown')) transitionOut = 'slideDown';
      else if (transitionType) transitionOut = 'fade';

      const timing = clipTimings[index];
      const overlayForClip = textOverlays.find(o => o.start >= timing.start && o.start < timing.end);
      let titlePosition: any;
      if (overlayForClip) {
        const pos = overlayForClip.position || 'center';
        if (pos.includes('top') && pos.includes('left')) titlePosition = 'top-left';
        else if (pos.includes('top') && pos.includes('right')) titlePosition = 'top-right';
        else if (pos.includes('top')) titlePosition = 'top-center';
        else if (pos.includes('bottom') && pos.includes('left')) titlePosition = 'bottom-left';
        else if (pos.includes('bottom') && pos.includes('right')) titlePosition = 'bottom-right';
        else if (pos.includes('bottom')) titlePosition = 'bottom-center';
        else if (pos.includes('left')) titlePosition = 'center-left';
        else if (pos.includes('right')) titlePosition = 'center-right';
        else titlePosition = 'center';
      }

      return {
        id: `ai-clip-${Date.now()}-${index}`,
        sourceFileId: matchingFile?.id || `unknown-${index}`,
        position: index,
        prompt: '',
        duration: clipLength,
        name: matchingFile?.name || `Clip ${index + 1}`,
        thumbnailUrl: matchingFile?.thumbnailUrl,
        fileType: 'video' as const,
        layerType: 'main' as const,
        inTime: trimStart,
        outTime: trimStart + clipLength,
        transitionOut,
        titleText: overlayForClip?.text || undefined,
        titlePosition: overlayForClip ? titlePosition : undefined,
      } as ProjectClip;
    });
  };

  const applyAIEditToTimeline = (shotstackJson: any, message?: string) => {
    const newClips = convertShotstackToTimelineClips(shotstackJson);
    if (newClips.length === 0) {
      toast({ title: 'No clips generated', description: 'The AI edit plan has no video clips', variant: 'destructive' });
      return;
    }
    setClips(newClips);
    setShowScriptPanel(false);
    const clipsWithText = newClips.filter(c => (c as any).titleText).length;
    toast({
      title: message || 'AI Edit Applied',
      description: clipsWithText > 0
        ? `${newClips.length} clips on the timeline (${clipsWithText} with text overlays). Adjust as needed and render.`
        : `${newClips.length} clips on the timeline. Adjust as needed and render.`,
    });
    // Compact preview strip of the generated edit
    handleQuickPreview(newClips);
  };
  applyAIEditToTimelineRef.current = applyAIEditToTimeline;

  // Included files → pseudo-clips when the timeline is empty (AI Create can
  // generate straight from selected assets).
  const buildClipsForAI = (): ProjectClip[] => {
    // The AI Create panel advertises "N assets included" from the CURRENT
    // selection — so the selection must drive the payload. Timeline clips
    // previously won unconditionally, which silently ignored selection
    // changes (a freshly added+checked asset never reached the AI while a
    // stale timeline clip did). Timeline clips are only the fallback when
    // nothing is selected.
    const selected = files
      .filter(f => selectedFiles.has(f.id) && ['video', 'audio', 'image'].includes(f.fileType || '') && f.layerType !== 'mask')
      .map((f, i) => ({
        id: `sel-${f.id}`,
        sourceFileId: f.id,
        position: i,
        prompt: f.hasGreenScreen ? 'chromaKey #97eca9' : '',
        duration: f.duration,
        name: f.name,
        thumbnailUrl: f.thumbnailUrl,
        fileType: f.fileType,
        layerType: (f.layerType === 'audio' ? 'audio' : (f.layerType || 'main')) as any,
        inTime: 0,
        outTime: f.duration || undefined,
      } as ProjectClip));
    if (selected.length > 0) return selected;
    return clips;
  };

  const scriptAnalysisPromptSection = (): string => {
    if (!scriptAnalysis || !scriptAnalysis.scenes || scriptAnalysis.scenes.length === 0) return '';
    return `

=== PRE-ANALYZED EDIT PLAN ===
The following scenes have been analyzed from the script with specific clip selections and trim times.
IMPORTANT: Use these exact clips and trim times as specified. Do not re-interpret or change the clip selections.

${scriptAnalysis.scenes.map((scene: any, idx: number) => {
  const clipDetails = scene.suggestedClips.map((clip: any) => {
    if (typeof clip === 'string') return `  - Clip: "${clip}"`;
    const trimInfo = clip.trim ? ` (trim: ${clip.trim.from}s to ${clip.trim.to}s)` : '';
    return `  - Clip: "${clip.assetName}"${trimInfo} - ${clip.reason}`;
  }).join('\n');
  return `Scene ${idx + 1}: ${scene.description}
Script Line: "${scene.scriptLine}"
Duration: ${scene.estimatedDuration}s
Clips:
${clipDetails}`;
}).join('\n\n')}

=== END EDIT PLAN ===
`;
  };

  const handleGenerateAIEdit = async () => {
    const sourceClips = buildClipsForAI();
    if (sourceClips.length === 0) {
      toast({ title: 'Nothing to edit', description: 'Add and select assets first', variant: 'destructive' });
      return;
    }
    setIsGeneratingAI(true);
    try {
      const { assetsMetadata } = buildRenderAssets({
        clips: sourceClips,
        files,
        analysisResults,
        includeTranscripts: true,
      });

      let aiPrompt = creativeBrief.trim() ? creativeBrief : generateMasterPrompt();
      aiPrompt += scriptAnalysisPromptSection();

      const outputConfig = resolveOutputConfig(aspectRatio, files);
      const effectiveRatio = aspectRatio === 'source'
        ? `${outputConfig.output_width}:${outputConfig.output_height}`
        : aspectRatio;

      const response = await fetch('/api/shotstack/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: currentProject,
          users_text: aiPrompt,
          users_objective: `Create an enhanced video using ${sourceClips.length} clips in ${effectiveRatio} aspect ratio`,
          assets_metadata: assetsMetadata,
          aspect_ratio: effectiveRatio,
          shotstack_resolution: outputConfig.shotstack_resolution || 'hd',
          output_width: outputConfig.output_width,
          output_height: outputConfig.output_height,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || `Preview generation failed (${response.status})`);
      }
      const data = await response.json();
      if (data.success && data.shotstackJson) {
        applyAIEditToTimeline(data.shotstackJson, 'AI Edit Generated');
      } else {
        throw new Error('Invalid preview response');
      }
    } catch (error: any) {
      toast({ title: 'Generation Failed', description: error.message || 'Failed to generate AI edit', variant: 'destructive' });
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // --- Render ----------------------------------------------------------------
  const handleRender = async () => {
    if (clips.length === 0) {
      toast({
        title: 'Nothing to render',
        description: renderMode === 'lambda' ? 'Generate an edit first, or add clips to the timeline.' : 'Add clips to the timeline first.',
        variant: 'destructive',
      });
      return;
    }

    const masterPrompt = isEditingMasterPrompt ? editableMasterPrompt : generateMasterPrompt();
    const totalDuration = clips.reduce((sum, clip) => sum + clip.duration, 0);
    const firstClipThumbnail = clips[0]?.thumbnailUrl && clips[0].thumbnailUrl !== 'AUDIO_ICON' && clips[0].thumbnailUrl !== 'SRT_ICON'
      ? clips[0].thumbnailUrl
      : undefined;

    const queuedVideo: RenderedVideo = {
      id: randId(),
      name: `Enhanced_${new Date().toISOString().split('T')[0]}_${Date.now()}`,
      thumbnailUrl: firstClipThumbnail,
      timestamp: new Date().toISOString(),
      duration: totalDuration,
      status: 'processing',
      progress: 0,
      configuration: {
        clips: clips.map(({ analysisData, ...clipWithoutAnalysis }: any) => clipWithoutAnalysis),
        masterPrompt,
      },
    };
    setRenderedVideos(prev => [...prev, queuedVideo]);
    toast({ title: 'Processing request', description: 'Your video has been added to the render queue' });
    setIsSubmittingRender(true);

    try {
      const { assetsMetadata, lumaMatteMetadata } = buildRenderAssets({
        clips,
        files,
        analysisResults,
        includeTranscripts: renderMode === 'lambda',
      });

      const outputConfig = resolveOutputConfig(aspectRatio, files);
      const effectiveRatio = aspectRatio === 'source'
        ? `${outputConfig.output_width}:${outputConfig.output_height}`
        : aspectRatio;

      const endpoint = renderMode === 'direct' ? '/api/shotstack/generate-direct' : '/api/shotstack/generate';
      let requestBody: any;

      if (renderMode === 'direct') {
        // Quick Render: explicit clip data for deterministic JSON generation
        const clipData = clips.map((clip: any) => {
          const file: any = files.find(f => f.id === clip.sourceFileId || (f as any)._id === clip.sourceFileId);
          const hasLumaMatte = file?.linkedMaskId || file?.greenScreenType === 'foreground-mask' || clip.linkedMaskId;
          const resolvedFileUrl = resolveVideoUrl(file?.s3Url, file?.s3Key);
          const resolvedClipUrl = resolveVideoUrl(clip.s3Url, clip.s3Key);
          const videoUrl = hasLumaMatte ? (resolvedFileUrl || resolvedClipUrl) : (file?.greenScreenUrl || resolvedFileUrl || resolvedClipUrl);
          const finalUrl = videoUrl || resolvedClipUrl || clip.url;

          return {
            id: clip.id,
            name: clip.name,
            fileType: clip.fileType,
            layerType: clip.layerType,
            duration: clip.duration || file?.duration || 10,
            inTime: clip.inTime,
            outTime: clip.outTime,
            volume: clip.volume ?? 1.0,
            url: finalUrl,
            s3Url: clip.s3Url || file?.s3Url,
            s3Key: clip.s3Key || file?.s3Key,
            titleText: clip.titleText,
            titlePosition: clip.titlePosition,
            titleFont: clip.titleFont,
            chromaKey: clip.chromaKey || (clip.prompt?.includes('chromaKey') ? {
              enabled: true,
              threshold: parseInt(clip.prompt.match(/threshold:(\d+)/)?.[1] || '100'),
              halo: parseInt(clip.prompt.match(/halo:(\d+)/)?.[1] || '30'),
            } : undefined),
            transitionOut: clip.transitionOut,
            imageMode: clip.imageMode,
            imagePosition: clip.imagePosition,
            imageDuration: clip.imageDuration,
            imageScale: clip.imageScale,
            isOverlay: clip.isOverlay,
            track: clip.track,
            stackedOn: clip.stackedOn,
            overlayPosition: clip.overlayPosition,
            overlayScale: clip.overlayScale,
            showCaptions: clip.showCaptions,
            captionStyle: clip.captionStyle,
            captionFont: clip.captionFont,
            captionBackground: clip.captionBackground,
            captionFontColor: clip.captionFontColor,
            captionStrokeColor: clip.captionStrokeColor,
            // Transcript words for caption timing: user-edited referenceText
            // wins (synthetic even spacing), else analysisData / analysisResults.
            transcriptWords: clip.showCaptions ? (() => {
              if (clip.referenceText && clip.referenceText.trim()) {
                const inTime = clip.inTime || 0;
                const outTime = clip.outTime || clip.duration || 10;
                const wordList = clip.referenceText.trim().split(/\s+/);
                const wordDuration = (outTime - inTime) / wordList.length;
                return wordList.map((word: string, idx: number) => ({
                  word,
                  start_time: inTime + idx * wordDuration,
                  end_time: inTime + (idx + 1) * wordDuration,
                }));
              }
              if (clip.analysisData?.transcript?.words?.length > 0) return clip.analysisData.transcript.words;
              if (file?.analysisData?.transcript?.words?.length > 0) return file.analysisData.transcript.words;
              if (clip.analysisData?.transcript?.segments?.length > 0) return clip.analysisData.transcript.segments;
              if (file?.analysisData?.transcript?.segments?.length > 0) return file.analysisData.transcript.segments;
              const fileAnalysis: any = findFileAnalysis(analysisResults, file?.id);
              if (fileAnalysis?.transcript?.words?.length > 0) return fileAnalysis.transcript.words;
              if (fileAnalysis?.transcript?.segments?.length > 0) return fileAnalysis.transcript.segments;
              return [];
            })() : undefined,
          };
        });

        requestBody = {
          clips: clipData,
          aspect_ratio: effectiveRatio,
          resolution: outputConfig.shotstack_resolution || 'hd',
          output_width: outputConfig.output_width,
          output_height: outputConfig.output_height,
          luma_matte_metadata: lumaMatteMetadata,
          filter: exportFilter !== 'none' ? exportFilter : undefined,
          colorAdjustments: hasColorAdjustments(colorAdjustments) ? colorAdjustments : undefined,
          template: activeTemplate ? {
            colors: activeTemplate.settings?.colors,
            fonts: activeTemplate.settings?.fonts,
            titles: activeTemplate.settings?.titles,
            timeline: activeTemplate.settings?.timeline,
            logo: activeTemplate.settings?.logo,
            audio: activeTemplate.settings?.audio,
          } : undefined,
        };
      } else {
        // AI Creative: NorthShore Lambda pipeline (assets_metadata + prompt)
        const displayRatio = aspectRatio === 'source'
          ? `${outputConfig.output_width}x${outputConfig.output_height} (Match Source)`
          : aspectRatio;
        const aspectRatioContext = `Output format: ${displayRatio} ${
          aspectRatio === '16:9' ? '(Landscape/YouTube)' :
          aspectRatio === '9:16' ? '(Portrait/TikTok/Reels)' :
          aspectRatio === '1:1' ? '(Square/Instagram)' :
          aspectRatio === '4:5' ? '(Instagram Portrait)' : ''
        }`;

        const hasGreenScreenVideo = clips.some((clip: any) => clip.prompt?.toLowerCase().includes('green screen') || clip.hasGreenScreen);
        const hasBackgroundLayer = clips.some((clip: any) => clip.layerType === 'background' || clip.prompt?.toLowerCase().includes('background'));
        let greenScreenInstructions = '';
        if (hasGreenScreenVideo && hasBackgroundLayer) {
          greenScreenInstructions = `\n\nIMPORTANT COMPOSITING INSTRUCTIONS:
- This project contains GREEN SCREEN videos that must have chromaKey effect applied
- Apply chromaKey with color #97eca9 (Replicate's green) to remove green backgrounds
- Place videos with chromaKey ABOVE background images/videos in the layer order
- Background images/videos should be on the BOTTOM layer (no chromaKey)
- Preserve all audio from original videos at full volume (1.0)`;
        }

        const enhancedPrompt = `${masterPrompt}${greenScreenInstructions}\n\n${aspectRatioContext}`;
        let aiPrompt = creativeBrief.trim() ? creativeBrief : enhancedPrompt;
        aiPrompt += scriptAnalysisPromptSection();

        requestBody = {
          company_name: currentProject,
          users_text: aiPrompt,
          users_objective: `Create an enhanced video using ${clips.length} clips in ${effectiveRatio} aspect ratio`,
          assets_metadata: assetsMetadata,
          luma_matte_metadata: lumaMatteMetadata,
          aspect_ratio: effectiveRatio,
          shotstack_resolution: outputConfig.shotstack_resolution || 'hd',
          output_width: outputConfig.output_width,
          output_height: outputConfig.output_height,
          filter: exportFilter !== 'none' ? exportFilter : undefined,
          colorAdjustments: hasColorAdjustments(colorAdjustments) ? colorAdjustments : undefined,
          // P1: pass the selected template so the AI edit's titles + captions render
          // on-brand (typography/color now; logo + intro/outro land in part 2).
          template: activeTemplate ? {
            colors: activeTemplate.settings?.colors,
            fonts: activeTemplate.settings?.fonts,
            titles: activeTemplate.settings?.titles,
            aiDefaults: activeTemplate.settings?.aiDefaults,
            timeline: activeTemplate.settings?.timeline,
            logo: activeTemplate.settings?.logo,
            audio: activeTemplate.settings?.audio,
          } : undefined,
          debug: false,
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || 'Failed to generate Shotstack JSON');
      }
      const result = await response.json();

      updateRender(queuedVideo.id, {
        status: result.renderStatus === 'submitted' ? 'rendering' : 'processing',
        renderId: result.renderId,
        renderStatus: result.renderStatus,
        shotstackJson: result.shotStackJson,
        progress: 10,
      });

      if (result.renderStatus === 'submitted' && result.renderId) {
        toast({
          title: 'Video Rendering Started!',
          description: renderMode === 'direct'
            ? 'Timeline render: Shotstack is processing your video.'
            : 'AI Create: Shotstack is rendering your enhanced video.',
        });
        pollRenderStatus(queuedVideo.id, result.renderId);
      }
    } catch (error: any) {
      console.error('Render failed:', error);
      updateRender(queuedVideo.id, { status: 'failed', error: error.message });
      toast({ title: 'Render Failed', description: error.message || 'An error occurred during processing', variant: 'destructive' });
    } finally {
      setIsSubmittingRender(false);
    }
  };

  // Render status polling — every 5s, 15 min timeout, 10-consecutive-error cap.
  // Uses functional updates + renderedVideosRef (fixes the legacy stale-closure bug).
  const pollRenderStatus = (videoId: string, renderId: string) => {
    const startTime = Date.now();
    const TIMEOUT_MS = 15 * 60 * 1000;
    let consecutiveErrors = 0;
    const MAX_ERRORS = 10;

    const pollInterval = setInterval(async () => {
      if (Date.now() - startTime > TIMEOUT_MS) {
        clearInterval(pollInterval);
        setRenderedVideos(prev => prev.map(v =>
          v.id === videoId && v.status !== 'rendered' && v.status !== 'failed'
            ? { ...v, status: 'failed' as const, error: 'Render timed out after 15 minutes' }
            : v));
        toast({ title: 'Render Timed Out', description: 'The video rendering took longer than expected and was cancelled.', variant: 'destructive' });
        return;
      }

      try {
        const response = await fetch(`/api/shotstack/render-status/${renderId}`);
        if (!response.ok) throw new Error('Failed to check render status');
        const statusData = await response.json();
        consecutiveErrors = 0;
        const status = statusData.status;
        const progress = statusData.progress || 0;

        setRenderedVideos(prev => prev.map(v =>
          v.id === videoId ? { ...v, renderStatus: status, progress, url: statusData.videoUrl } : v));

        if (status === 'done') {
          clearInterval(pollInterval);
          const currentVideo = renderedVideosRef.current.find(v => v.id === videoId);
          const needsAudioEnhancement = currentVideo?.finishingTouches?.enhanceAudio;

          setRenderedVideos(prev => prev.map(v => v.id === videoId ? {
            ...v,
            status: 'rendered' as const,
            url: statusData.videoUrl,
            progress: 100,
            pipelineStage: (needsAudioEnhancement ? 'enhancing-audio' : 'complete') as any,
          } : v));

          if (needsAudioEnhancement && statusData.videoUrl && currentVideo) {
            toast({ title: 'Video rendered — enhancing audio…', description: 'Automatically starting audio enhancement as requested.' });
            audioEnhancement.handleEnhanceAudio({
              ...currentVideo,
              status: 'rendered',
              url: statusData.videoUrl,
              videoUrl: statusData.videoUrl,
            });
          } else {
            toast({ title: 'Video Ready!', description: 'Your enhanced video has been rendered successfully.' });
          }
        } else if (status === 'failed') {
          clearInterval(pollInterval);
          const errorMessage = statusData.errorDetails?.error || statusData.errorDetails?.message || statusData.error || 'Render failed';
          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId ? { ...v, status: 'failed' as const, error: errorMessage, errorDetails: statusData.errorDetails } : v));
          toast({ title: 'Render Failed', description: errorMessage, variant: 'destructive' });
        }
      } catch (error) {
        consecutiveErrors++;
        console.error(`Error polling render status (${consecutiveErrors}/${MAX_ERRORS}):`, error);
        if (consecutiveErrors >= MAX_ERRORS) {
          clearInterval(pollInterval);
          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId && v.status !== 'rendered' && v.status !== 'failed'
              ? { ...v, status: 'failed' as const, error: 'Lost connection to render service after multiple retries' }
              : v));
          toast({ title: 'Render Failed', description: 'Lost connection to the render service. Please try again.', variant: 'destructive' });
        }
      }
    }, 5000);
  };
  pollRenderStatusRef.current = pollRenderStatus;

  // Resume polling for in-progress renders on mount
  useEffect(() => {
    const inProgress = renderedVideosRef.current.filter(v =>
      (v.status === 'rendering' || v.status === 'processing') && v.renderId);
    inProgress.forEach(video => {
      if (video.renderId) pollRenderStatus(video.id, video.renderId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Render history actions -------------------------------------------------
  const handleDeleteRenderedVideo = (videoId: string) => {
    if (!window.confirm('Remove this video from the history?')) return;
    setRenderedVideos(prev => prev.filter(v => v.id !== videoId));
    toast({ title: 'Video removed', description: 'The video has been removed from the history' });
  };

  const handleSaveToLibrary = async (video: RenderedVideo) => {
    if (!video.url) {
      toast({ title: 'Cannot save', description: 'Video URL is not available', variant: 'destructive' });
      return;
    }
    toast({ title: 'Saving to library…', description: 'Transferring video to permanent storage' });
    try {
      const response = await fetch('/api/transfer-to-s3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl: video.url, fileName: video.name }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Transfer failed');
      }
      const result = await response.json();
      const savedRender: RenderedVideo = {
        ...video,
        savedAt: new Date().toISOString(),
        savedS3Url: result.s3Url,
        savedS3Key: result.s3Key,
      };
      setSavedRenders(prev => [...prev, savedRender]);
      setRenderedVideos(prev => prev.filter(v => v.id !== video.id));
      toast({ title: 'Saved to library', description: `"${video.name}" has been permanently saved` });
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Failed to save video to library',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteFromLibrary = (videoId: string) => {
    if (!window.confirm('Remove this video from your library? The file will remain in S3.')) return;
    setSavedRenders(prev => prev.filter(v => v.id !== videoId));
    toast({ title: 'Removed from library', description: 'The video has been removed from your library' });
  };

  const handleReimportRenderedVideo = async (video: RenderedVideo) => {
    if (!video.url) {
      toast({ title: 'Error', description: 'Video URL not available', variant: 'destructive' });
      return;
    }
    toast({ title: 'Transferring…', description: 'Uploading video to permanent storage' });
    try {
      const response = await fetch('/api/transfer-to-s3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl: video.url, fileName: video.name }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Transfer failed');
      }
      const result = await response.json();
      const newFile: EnhancerFile = {
        id: randId(),
        name: `${video.name} (Rendered)`,
        fileType: 'video',
        thumbnailUrl: video.thumbnailUrl || '',
        duration: video.duration || 0,
        timestamp: new Date().toISOString(),
        s3Url: result.s3Url,
        s3Key: result.s3Key,
        imported: true,
        enhanced: true,
        enhancementPrompt: video.configuration.masterPrompt || 'Re-imported from render',
      };
      addFilesToBin([newFile]);
      toast({ title: 'Re-imported', description: `"${video.name}" added to your assets with permanent storage` });
    } catch (error) {
      toast({
        title: 'Transfer Failed',
        description: error instanceof Error ? error.message : 'Failed to transfer video',
        variant: 'destructive',
      });
    }
  };

  const downloadJson = (video: RenderedVideo) => {
    const jsonStr = JSON.stringify(video.configuration, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${video.name}_configuration.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: 'JSON downloaded', description: `Configuration saved as ${video.name}_configuration.json` });
  };

  // --- Sign-in gate -----------------------------------------------------------
  if (!isAuthenticated) {
    return (
      <main
        className="mx-auto"
        style={{ maxWidth: 1440, padding: '0 16px 96px', background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}
      >
        <div className="mx-auto" style={{ maxWidth: 540, padding: '120px 16px 0' }}>
          <div className="text-center" style={{ padding: '40px 32px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
            <div
              className="mx-auto flex items-center justify-center"
              style={{ width: 64, height: 64, borderRadius: 999, background: 'var(--hj-blue-tint)', color: 'var(--hj-yellow-ink)' }}
            >
              <FolderOpen className="w-7 h-7" />
            </div>
            <h2 className="font-hj-display m-0" style={{ marginTop: 16, fontSize: 28, color: 'var(--hj-ink)', letterSpacing: '-0.02em' }}>
              Sign in to keep editing.
            </h2>
            <p className="m-0" style={{ marginTop: 12, color: 'var(--hj-ink-3)', fontSize: 14, lineHeight: 1.55 }}>
              AI Creative needs your account to reach cloud storage, transcripts, and renders.
            </p>
            <button
              type="button"
              onClick={() => setShowLoginModal(true)}
              className="inline-flex items-center justify-center gap-2"
              style={{
                marginTop: 22, padding: '11px 22px', borderRadius: 999,
                background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)',
                color: 'oklch(0.18 0.005 70)', fontSize: 14, fontWeight: 700,
                fontFamily: 'inherit', cursor: 'pointer', minHeight: 44,
              }}
            >
              Sign in
            </button>
          </div>
        </div>
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onSuccess={() => { setShowLoginModal(false); setIsAuthenticated(true); }}
        />
      </main>
    );
  }

  // --- Layout -------------------------------------------------------------------
  const includedCount = files.filter(f => selectedFiles.has(f.id) && f.layerType !== 'mask').length;

  const renderControlsProps = {
    showMasterPrompt, setShowMasterPrompt,
    isEditingMasterPrompt, setIsEditingMasterPrompt,
    editableMasterPrompt, setEditableMasterPrompt,
    generateMasterPrompt,
    onMasterPromptSaved: () => toast({ title: 'Master prompt saved', description: 'Your custom prompt will be used for rendering' }),
    aspectRatio, setAspectRatio,
    renderMode,
    onRenderModeToggle: () => setRenderMode(renderMode === 'direct' ? 'lambda' : 'direct'),
    activeTemplate, setActiveTemplate,
    onShowTemplates: () => setShowTemplatesPanel(true),
    exportFilter, setExportFilter,
    colorAdjustments, setColorAdjustments,
    editPlan: planMode.editPlan,
    isPlanApproved: planMode.isPlanApproved,
    planModeActive,
    isPlanProcessing: planMode.isPlanProcessing,
    onStartPlanMode: planMode.handleStartPlanMode,
    scriptFileName: scriptHook.scriptFileName,
    filesCount: files.length,
    clipsCount: clips.length,
    isLoadingPreview: isGeneratingAI,
    onPreviewAI: handleGenerateAIEdit,
    onQuickPreview: () => handleQuickPreview(),
    onRender: handleRender,
  };

  return (
    <div style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: '100vh' }}>
      <EnhancerTopBar
        projectHeader={{
          projects,
          currentProject,
          activeTemplate,
          showTemplatesPanel,
          isAuthenticated,
          onProjectChange: handleProjectChange,
          onCreateProject: handleCreateProject,
          onRenameProject: handleRenameProject,
          onDeleteProject: handleDeleteProject,
          onImport: () => (isAuthenticated ? setShowImporter(true) : setShowLoginModal(true)),
          // Templates hidden per Joe (Jul 15) — the whole panel is parked.
          onToggleTemplates: undefined,
          getRootProps: headerDropzone.getRootProps,
          getInputProps: headerDropzone.getInputProps,
        }}
        renderMode={renderMode}
        onModeChange={setRenderMode}
        aspectRatio={aspectRatio}
        onAspectRatioChange={setAspectRatio}
        creditsLabel={getCreditDisplay()}
        isRendering={isSubmittingRender}
        onRender={handleRender}
      />

      <div className="mx-auto" style={{ maxWidth: 1280, padding: '16px 16px 96px' }}>
        {/* Breadcrumb */}
        <div className="flex items-center gap-2.5" style={{ padding: '4px 0 14px', fontSize: 13 }}>
          <a href="/" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>
            <ChevronLeft size={12} strokeWidth={2} /> Home
          </a>
          <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
          <span style={{ color: 'var(--hj-ink)' }}>AI Creative</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4 items-start">
          {/* Assets */}
          <AssetsPanel
            files={files}
            selectedFiles={selectedFiles}
            analysisResults={analysisResults}
            renderMode={renderMode}
            aiGeneratingJob={aiGeneratingJob}
            onToggleSelect={handleToggleSelect}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            dropzone={{
              getRootProps: mainDropzone.getRootProps,
              getInputProps: mainDropzone.getInputProps,
              isDragActive: mainDropzone.isDragActive,
            }}
            onOpenLibrary={() => setShowLibraryPicker(true)}
            onOpenImporter={() => (isAuthenticated ? setShowImporter(true) : setShowLoginModal(true))}
            onOpenAIGenerator={() => setShowAIGenerator(true)}
            onDragStart={() => { /* dataTransfer carries the fileId */ }}
            onAddToTimeline={renderMode === 'direct' ? (file) => handleClipDrop(file.id) : undefined}
            onAddToTimelineWithOptions={renderMode === 'direct' ? (file, options) => handleClipDropWithOptions(file.id, options) : undefined}
            onInsertText={renderMode === 'direct' ? handleInsertText : undefined}
            onFileRemove={handleFileRemove}
            onTranscriptEdit={handleTranscriptEdit}
            onSpeakerEdit={handleSpeakerEdit}
            onAddSelectedToTimeline={handleAddSelectedToTimeline}
            onRemoveBackground={(fileIds) => bgRemoval.handleRemoveBackground(fileIds)}
          />

          {/* Mode panel */}
          <div className="flex flex-col gap-4 min-w-0">
            {renderMode === 'lambda' ? (
              <AICreatePanel
                files={files}
                includedCount={includedCount}
                creativeBrief={creativeBrief}
                onCreativeBriefChange={setCreativeBrief}
                generatedPrompt={generateMasterPrompt()}
                script={scriptHook}
                plan={planMode}
                aspectRatio={aspectRatio}
                onAspectRatioChange={setAspectRatio}
                isGenerating={isGeneratingAI}
                onGenerate={handleGenerateAIEdit}
                hasTimelineEdit={clips.length > 0}
              />
            ) : (
              <TimelinePanel
                timeline={{
                  clips,
                  files: files as any,
                  analysisResults,
                  onClipDrop: handleClipDrop,
                  onClipRemove: handleClipRemove,
                  onClipReorder: handleClipReorder,
                  onPromptChange: handlePromptChange,
                  onTimeChange: handleTimeChange,
                  onPreviewClip: handlePreviewClip,
                  onTitleChange: handleTitleChange,
                  onVolumeChange: handleVolumeChange,
                  onTransitionChange: handleTransitionChange,
                  onEffectChange: handleEffectChange,
                  onImageSettingsChange: handleImageSettingsChange,
                  onCaptionsChange: handleCaptionsChange,
                  onReferenceTextChange: handleReferenceTextChange,
                  onLayerTypeChange: handleLayerTypeChange,
                  onStackClip: handleStackClip,
                  onUnstackClip: handleUnstackClip,
                  onOverlaySettingsChange: handleOverlaySettingsChange,
                  renderMode,
                }}
                renderControls={renderControlsProps}
                clipsCount={clips.length}
                templates={{
                  show: showTemplatesPanel,
                  onClose: () => setShowTemplatesPanel(false),
                  onApply: (template) => {
                    setActiveTemplate(template);
                    toast({ title: 'Template Applied', description: `"${template.name}" settings will be used for rendering.` });
                  },
                  active: activeTemplate,
                  files,
                }}
              />
            )}

            {/* Quick preview strip (both modes) */}
            {showQuickPreviewPanel && quickPreview && (
              <QuickPreviewPanel
                quickPreview={quickPreview}
                onClose={() => { setShowQuickPreviewPanel(false); setQuickPreview(null); }}
                onRender={() => { setShowQuickPreviewPanel(false); handleRender(); }}
              />
            )}
          </div>
        </div>

        {/* Render history */}
        {(renderedVideos.length > 0 || savedRenders.length > 0) && (
          <section style={{ ...cardStyle, marginTop: 20 }}>
            <div className="mono-label" style={{ marginBottom: 12, color: 'var(--hj-ink-3)' }}>Renders</div>
            <RenderHistory
              renderedVideos={renderedVideos}
              savedRenders={savedRenders}
              youtubeConnected={youtube.youtubeConnected}
              onViewVideo={(video) => setSelectedVideoModal(video)}
              onViewJson={(video) => setSelectedJsonView(video)}
              onDownload={(url) => window.open(url, '_blank')}
              onSchedule={(video) => {
                setScheduleVideoData({ videoUrl: video.url!, thumbnailUrl: video.thumbnailUrl, renderId: video.renderId });
                setShowScheduleModal(true);
              }}
              onYoutubePublish={(video) => youtube.openYoutubePublishModal(video)}
              onReimport={(video) => handleReimportRenderedVideo(video)}
              onSaveToLibrary={(video) => handleSaveToLibrary(video)}
              onDeleteFromHistory={(videoId) => handleDeleteRenderedVideo(videoId)}
              onDeleteFromLibrary={(videoId) => handleDeleteFromLibrary(videoId)}
              onRemoveFailedRender={(videoId) => setRenderedVideos(prev => prev.filter(v => v.id !== videoId))}
              onViewErrorDetails={(video) => {
                const errorInfo: any = video.errorDetails || { error: video.error || 'Unknown error' };
                alert(`Render Error Details:\n\nError: ${errorInfo.error || 'Unknown'}\nMessage: ${errorInfo.message || 'No additional details'}\n\nFull Details:\n${JSON.stringify(errorInfo.details || {}, null, 2)}`);
              }}
              onEnhanceAudio={(video, mode) => audioEnhancement.executeEnhanceAudio(video, mode)}
              onRemoveBackground={(video) => bgReplacement.executeRemoveBackground(video)}
              onIteratePlan={(video) => planMode.loadPlanFromRender(video)}
            />
          </section>
        )}
      </div>

      {/* ---------------- Modals ---------------- */}

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={() => { setShowLoginModal(false); setIsAuthenticated(true); setShowImporter(true); }}
      />

      <CastingVideoImporter
        open={showImporter}
        onOpenChange={(open) => {
          setShowImporter(open);
          if (!open) setIsAuthenticated(document.cookie.includes('token='));
        }}
        onVideosImported={handleVideosImported}
      />

      <LibraryPicker
        open={showLibraryPicker}
        stageTarget="permanent"
        onClose={() => setShowLibraryPicker(false)}
        onPick={handleLibraryPick}
      />

      <AIAssetGenerator
        isOpen={showAIGenerator}
        onClose={() => setShowAIGenerator(false)}
        onGenerate={handleAIGenerate}
        timelineDuration={clips.reduce((sum, clip) => sum + clip.duration, 0) || undefined}
      />

      {/* First-visit mode explainer (replaces the legacy welcome dialog + tour) */}
      <AlertDialog open={showModeExplainer} onOpenChange={setShowModeExplainer}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Welcome to the Video Enhancer</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-2">
              <span className="block">Two ways to work — switch anytime from the top bar:</span>
              <span className="block border rounded-lg p-3">
                <strong className="flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> AI Create</strong>
                <span className="block mt-1 text-xs">Describe what you want; the AI reads your transcripts and cuts the edit for you.</span>
              </span>
              <span className="block border rounded-lg p-3">
                <strong className="flex items-center gap-1.5"><SlidersHorizontal className="h-3.5 w-3.5" /> Timeline</strong>
                <span className="block mt-1 text-xs">Full manual control — cuts, titles, transitions, captions, and effects.</span>
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="w-full sm:flex-1"
              onClick={() => {
                setRenderMode('direct');
                localStorage.setItem('hasSeenWelcomeDialog', 'true');
                setShowModeExplainer(false);
              }}
            >
              Start in Timeline
            </Button>
            <Button
              className="w-full sm:flex-1"
              onClick={() => {
                setRenderMode('lambda');
                localStorage.setItem('hasSeenWelcomeDialog', 'true');
                setShowModeExplainer(false);
              }}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Start with AI Create
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Casting import choice */}
      <Dialog open={showImportChoice} onOpenChange={setShowImportChoice}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Videos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium">{pendingImportData?.projectName}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {pendingImportData?.videos.length} video{pendingImportData?.videos.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="space-y-3">
              <Button variant="default" className="w-full justify-start" onClick={() => processImport(true)}>
                Create new project "{pendingImportData?.projectName}"
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => processImport(false)}>
                Add to current project "{currentProject}"
              </Button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => { setShowImportChoice(false); setPendingImportData(null); }}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove BG output type (assets bin flow) */}
      <Dialog open={bgRemoval.showRemoveBgModal} onOpenChange={bgRemoval.setShowRemoveBgModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Background</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Choose how to remove the background from your video:</p>
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="outputType"
                  value="foreground-mask"
                  checked={bgRemoval.selectedOutputType === 'foreground-mask'}
                  onChange={(e) => bgRemoval.setSelectedOutputType(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-sm">Foreground Mask (Recommended)</div>
                  <p className="text-xs text-muted-foreground">Black & white mask for Shotstack compositing. Best edge quality, no color artifacts.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="outputType"
                  value="green-screen"
                  checked={bgRemoval.selectedOutputType === 'green-screen'}
                  onChange={(e) => bgRemoval.setSelectedOutputType(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-sm">Green Screen</div>
                  <p className="text-xs text-muted-foreground">Replace background with green for chromaKey removal. Best for standard compositing.</p>
                </div>
              </label>
            </div>
            <p className="text-sm text-muted-foreground">
              <strong>Processing {bgRemoval.pendingRemoveBgFiles.length} video{bgRemoval.pendingRemoveBgFiles.length !== 1 ? 's' : ''}</strong>
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => bgRemoval.setShowRemoveBgModal(false)}>Cancel</Button>
            <Button onClick={() => { bgRemoval.setShowRemoveBgModal(false); bgRemoval.processRemoveBackground(bgRemoval.selectedOutputType); }}>
              Remove Background
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove BG output type (rendered video flow) */}
      <Dialog open={bgReplacement.outputTypeDialogOpen} onOpenChange={bgReplacement.setOutputTypeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Background</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Choose how to remove the background from your video:</p>
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="renderedOutputType"
                  value="foreground-mask"
                  checked={bgReplacement.selectedOutputType === 'foreground-mask'}
                  onChange={() => bgReplacement.setSelectedOutputType('foreground-mask')}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-sm">Foreground Mask (Recommended)</div>
                  <p className="text-xs text-muted-foreground">Black & white mask for Shotstack compositing. Best edge quality, no color artifacts.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="renderedOutputType"
                  value="green-screen"
                  checked={bgReplacement.selectedOutputType === 'green-screen'}
                  onChange={() => bgReplacement.setSelectedOutputType('green-screen')}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-sm">Green Screen</div>
                  <p className="text-xs text-muted-foreground">Replace background with green for chromaKey removal. Best for standard compositing.</p>
                </div>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => bgReplacement.setOutputTypeDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => bgReplacement.confirmRemoveBackground()}>Remove Background</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Background replacement wizard */}
      <BackgroundReplacementWizard
        open={bgReplacement.wizardOpen}
        step={bgReplacement.wizardStep}
        video={bgReplacement.targetVideo}
        maskUrl={bgReplacement.maskUrl}
        projectFiles={files as any}
        onClose={bgReplacement.closeWizard}
        onKeepGreenScreen={bgReplacement.keepGreenScreen}
        onSelectBackground={() => bgReplacement.setWizardStep('select-background')}
        onComposite={(url, type) => bgReplacement.executeCompositeBackground(url, type)}
        onGenerateAsset={(type, prompt, options) => handleAIGenerate(type, prompt, options)}
        generatingJob={aiGeneratingJob}
      />

      {/* JSON view */}
      <Dialog open={!!selectedJsonView} onOpenChange={() => setSelectedJsonView(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Configuration JSON - {selectedJsonView?.name}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh]">
            <pre className="text-xs p-4 bg-muted rounded-lg">
              {selectedJsonView && JSON.stringify(selectedJsonView.configuration, null, 2)}
            </pre>
          </ScrollArea>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSelectedJsonView(null)}>Close</Button>
            <Button onClick={() => selectedJsonView && downloadJson(selectedJsonView)}>
              <Download className="w-4 h-4 mr-2" />
              Download JSON
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clip preview (loops the trimmed range) */}
      <Dialog open={!!previewClip} onOpenChange={() => setPreviewClip(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-3">
            <DialogTitle>
              Clip Preview - {previewClip?.file.name}
              {previewClip?.clip.inTime !== undefined && previewClip?.clip.outTime !== undefined && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({previewClip.clip.inTime}s - {previewClip.clip.outTime}s)
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            {previewClip && previewClip.file.s3Url && (
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                <video
                  ref={(video) => {
                    if (video && previewClip.clip.inTime !== undefined) {
                      video.currentTime = previewClip.clip.inTime;
                      const handleTimeUpdate = () => {
                        if (previewClip.clip.outTime !== undefined && video.currentTime >= previewClip.clip.outTime) {
                          video.currentTime = previewClip.clip.inTime || 0;
                        }
                      };
                      video.addEventListener('timeupdate', handleTimeUpdate);
                    }
                  }}
                  src={resolveVideoUrl(previewClip.file.s3Url, previewClip.file.s3Key) || previewClip.file.s3Url}
                  controls
                  autoPlay
                  className="w-full h-full"
                />
              </div>
            )}
            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
              <p><strong>In:</strong> {previewClip?.clip.inTime ?? 'Start'}s · <strong>Out:</strong> {previewClip?.clip.outTime ?? 'End'}s</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rendered video playback */}
      <Dialog open={!!selectedVideoModal} onOpenChange={() => setSelectedVideoModal(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-3">
            <DialogTitle>Video Playback - {selectedVideoModal?.name}</DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            {selectedVideoModal && selectedVideoModal.url && (
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                <video src={selectedVideoModal.url} controls autoPlay className="w-full h-full" poster={selectedVideoModal.thumbnailUrl} />
              </div>
            )}
            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
              <p><strong>Duration:</strong> {Math.round(selectedVideoModal?.duration || 0)}s</p>
              <p><strong>Clips:</strong> {selectedVideoModal?.configuration.clips.length || 0}</p>
              <p><strong>Rendered:</strong> {selectedVideoModal ? new Date(selectedVideoModal.timestamp).toLocaleString() : ''}</p>
            </div>
            <div className="flex justify-between items-center mt-6">
              <Button variant="outline" onClick={() => setSelectedVideoModal(null)}>Close</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => selectedVideoModal && setSelectedJsonView(selectedVideoModal)}>View JSON</Button>
                {selectedVideoModal?.url && (
                  <Button onClick={() => selectedVideoModal.url && window.open(selectedVideoModal.url, '_blank')}>
                    <Download className="w-4 h-4 mr-2" />
                    Download Video
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule post */}
      {scheduleVideoData && (
        <SchedulePostModal
          isOpen={showScheduleModal}
          onClose={() => { setShowScheduleModal(false); setScheduleVideoData(null); }}
          onSchedule={async (data) => {
            const response = await fetch('/api/scheduled-posts', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to schedule post');
            toast({ title: 'Post Scheduled', description: 'Your video has been scheduled for posting.' });
          }}
          videoUrl={scheduleVideoData.videoUrl}
          thumbnailUrl={scheduleVideoData.thumbnailUrl}
          renderId={scheduleVideoData.renderId}
        />
      )}

      {/* YouTube publish */}
      <YouTubePublishDialog youtube={youtube} />

      {/* Credits */}
      <CreditConfirmDialog
        open={creditConfirmOpen}
        onOpenChange={setCreditConfirmOpen}
        operationType={creditConfirmOperation}
        operationName={creditConfirmOperationName}
        onConfirm={() => {
          if (creditConfirmCallback) creditConfirmCallback();
          fetchBalance();
        }}
        onCancel={() => setCreditConfirmCallback(null)}
      />
      <CreditPurchaseModal open={showCreditPurchaseModal} onOpenChange={setShowCreditPurchaseModal} />
      <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} trigger="ai_feature" />
    </div>
  );
};
